<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
  watch,
} from 'vue';
import { useMediaQuery, useResizeObserver } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';

import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useImageZoom } from 'dashboard/composables/useImageZoom';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { isLottieStickerAttachment } from 'dashboard/helper/lottieStickerPlayer';
import {
  isStickerAttachment,
  isStickerMessage,
} from 'dashboard/helper/stickerMessageHelper';
import { isGifAttachment } from 'dashboard/helper/gifMediaHelper';
import {
  useGuardedMedia,
  withPlaybackVariant,
} from 'dashboard/composables/useGuardedMedia';
import { audioPlaybackSource } from 'dashboard/helper/audioAttachmentHelper';
import { gifFrameStyle } from 'dashboard/components-next/message/bubbles/mediaAspectLayout';
import LottieStickerCanvas from 'dashboard/components-next/message/bubbles/LottieStickerCanvas.vue';
import {
  galleryReplyOpen,
  registerMediaViewer,
  setContactMediaReturn,
  unregisterMediaViewer,
} from 'dashboard/composables/aceleresMediaViewerCoordinator';
import { emitter } from 'shared/helpers/mitt';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { downloadFile } from '@chatwoot/utils';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';

import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import MessageReactionBar from 'dashboard/components/widgets/conversation/MessageReactionBar.vue';
import MessageReactions from 'dashboard/components-next/message/MessageReactions.vue';
import WhatsAppEmojiPicker from 'dashboard/components/widgets/conversation/WhatsAppEmojiPicker.vue';
import MobileEmojiSheet from 'dashboard/components-next/emoji/MobileEmojiSheet.vue';
import ForwardMessageModal from 'dashboard/modules/conversations/components/ForwardMessageModal.vue';
import FormattedContent from 'dashboard/components-next/message/bubbles/Text/FormattedContent.vue';
import PinDurationDialog from 'dashboard/components-next/message/PinDurationDialog.vue';
import { isMessagePinned } from 'dashboard/helper/pinnedMessages';
import {
  GALLERY_MOBILE_MEDIA_QUERY,
  getGalleryCaptionPlacement,
  getGalleryMediaFrame,
  getGalleryMediaActionStyle,
} from './galleryCaptionLayout';

const props = defineProps({
  teleportTarget: {
    type: String,
    default: '.conversation-details-wrap',
  },
  attachment: {
    type: Object,
    required: true,
  },
  allAttachments: {
    type: Array,
    required: true,
  },
  mobileAttachments: {
    type: Array,
    default: () => [],
  },
  mobileOverviewAttachments: {
    type: Array,
    default: () => [],
  },
  // Aceleres 2026-08-17: quem abre sabe se e figurinha (Image.vue ja resolve
  // isso por content_type/wame_media_type). Repassar e mais barato e mais
  // fiavel do que readivinhar aqui pelo anexo cru.
  isSticker: {
    type: Boolean,
    default: false,
  },
  // Aceleres: a bolha que abre a galeria ja calcula a legenda corretamente
  // (usa o content da propria mensagem). Repassamos aqui como fallback pro
  // anexo inicial porque o lookup de activeMessage via selectedChat.messages
  // pode nao encontrar a mensagem (ex.: paginacao/timing), deixando a
  // legenda em branco mesmo quando ela existe.
  initialCaption: {
    type: String,
    default: '',
  },
  // Aceleres: mesmo motivo do initialCaption acima — fallback pro GIF do
  // WhatsApp (chega como MP4 mudo) nao virar player de vídeo com controles
  // quando o lookup de activeMessage falha (pedido 2026-08-11).
  initialIsGif: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'error']);
const show = defineModel('show', { type: Boolean, default: false });
const isReplyingHere = ref(false);
// Cada bolha de midia tem o seu GalleryView: so a instancia que abriu a
// resposta mexe no estado partilhado.
const stopReplyingHere = () => {
  if (!isReplyingHere.value) return;
  isReplyingHere.value = false;
  galleryReplyOpen.value = false;
};

const { t, locale } = useI18n();
const isMobileViewport = useMediaQuery(GALLERY_MOBILE_MEDIA_QUERY);
const MOBILE_MEDIA_REQUEST_TIMEOUT_MS = 15000;
const MOBILE_MAX_CONCURRENT_ORIGINAL_DOWNLOADS = 2;
// Aceleres 2026-08-18: as midias do historico chegam pelo endpoint guardado
// (`/attachment_downloads/...`, ver attachments.json.jbuilder), que reavalia a
// permissao a cada request e por isso exige os cabecalhos de autenticacao. Num
// `<img src>` o navegador pede sem cabecalho e leva 401 — era isso que fazia as
// fotos antigas nunca aparecerem na tira. O mesmo resolvedor do painel de
// contato baixa pelo cliente autenticado e devolve um object URL.
const {
  isGuardedMediaUrl,
  withThumbVariant,
  guardedMediaSrc,
  guardedMediaFailed,
  retryGuardedMedia,
  resolveGuardedMedia,
  releaseGuardedMedia,
} = useGuardedMedia(
  isMobileViewport.value
    ? {
        requestTimeoutMs: MOBILE_MEDIA_REQUEST_TIMEOUT_MS,
        maxConcurrentOriginalDownloads:
          MOBILE_MAX_CONCURRENT_ORIGINAL_DOWNLOADS,
      }
    : undefined
);
const store = useStore();
const getters = useStoreGetters();
const ACELERES_API =
  typeof window !== 'undefined'
    ? resolveAceleresApiBase()
    : 'https://aceleres.ai';

const ALLOWED_FILE_TYPES = {
  IMAGE: 'image',
  VIDEO: 'video',
  IG_REEL: 'ig_reel',
  AUDIO: 'audio',
};

const galleryLabels = {
  previousImage: 'Imagem anterior',
  nextImage: 'Imagem seguinte',
  showInChatAction: 'Ver no chat',
  reply: 'Responder',
  removeStar: 'Remover destaque',
  star: 'Destacar',
  unpin: 'Desafixar mensagem',
  pin: 'Fixar mensagem',
  react: 'Reagir',
  decreaseZoom: 'Diminuir zoom',
  increaseZoom: 'Aumentar zoom',
  forward: 'Reenviar',
  forwardMobile: 'Encaminhar',
  allMedia: 'Todas as mídias',
  goToMessage: 'Ir para a mensagem',
  back: 'Voltar',
  moreOptions: 'Mais opções',
  videoProgress: 'Progresso do vídeo',
  playbackSpeed: 'Velocidade de reprodução',
  enterFullscreen: 'Tela cheia',
  exitFullscreen: 'Sair da tela cheia',
  volume: 'Volume',
  mute: 'Silenciar',
  unmute: 'Ativar som',
  pause: 'Pausar',
  play: 'Reproduzir',
  images: 'Imagens',
  navigationHint: 'A miniatura ativa é centralizada automaticamente',
  mediaUnavailable: 'Não foi possível carregar esta mídia.',
  retryMedia: 'Tentar novamente',
};

const galleryNavigationButtonClass = [
  // Aceleres 2026-09-01: `gallery-nav-btn` existe para dar um nome estavel a
  // estes dois botoes. Sem ele, a unica forma de os apanhar de fora era pela
  // classe utilitaria da cor (`!bg-[#6c6c6c]`), que muda com o desenho.
  'gallery-nav-btn',
  'z-10 pointer-events-auto !rounded-full !bg-[#6c6c6c] !text-white',
  'hover:enabled:!bg-[#858585] hover:enabled:scale-105',
  'focus-visible:!bg-[#858585] focus-visible:scale-105',
  'disabled:!bg-n-slate-5 disabled:!text-white [&>span]:!size-6',
].join(' ');

const isDownloading = ref(false);
const pendingMessageAction = ref('');
const activeAttachment = ref({});
const activeFileType = ref('');
const unavailableThumbnailKeys = ref(new Set());
const showReactionPicker = ref(false);
const showFullEmojiPicker = ref(false);
const showForwardModal = ref(false);
const showMobileMenu = ref(false);
const showMobileMediaOverview = ref(false);
const isMobileChromeVisible = ref(true);
const mobileMediaScale = ref(1);

const videoRef = useTemplateRef('videoRef');
const videoWrapperRef = useTemplateRef('videoWrapperRef');
const isVideoPlaying = ref(false);
const videoCurrentTime = ref(0);
const videoDuration = ref(0);
const videoVolume = ref(1);
const isVideoMuted = ref(false);
const videoPlaybackRate = ref(1);
const showVideoControls = ref(false);
const isVideoPointerHovering = ref(false);
const isVideoFullscreen = ref(false);
const isSeekingVideo = ref(false);
const videoAspectRatio = ref('16 / 9');
const PLAYBACK_RATE_STEPS = [1, 1.5, 2];
let videoControlsHideTimer = null;

const isSameAttachment = (first, second) => {
  if (first?.id && second?.id) return first.id === second.id;
  return (
    (first?.message_id || first?.messageId) ===
      (second?.message_id || second?.messageId) &&
    (first?.file_type || first?.fileType) ===
      (second?.file_type || second?.fileType) &&
    (first?.data_url || first?.dataUrl) ===
      (second?.data_url || second?.dataUrl)
  );
};

const attachmentKey = attachment =>
  String(
    attachment?.id ||
      `${attachment?.message_id || attachment?.messageId || 'message'}-${
        attachment?.data_url ||
        attachment?.dataUrl ||
        attachment?.thumb_url ||
        attachment?.thumbUrl ||
        attachment?.file_type ||
        attachment?.fileType ||
        'media'
      }`
  );

const mediaSourceCandidates = attachment =>
  [
    attachment?.data_url,
    attachment?.dataUrl,
    attachment?.thumb_url,
    attachment?.thumbUrl,
  ].filter(
    (source, index, sources) => source && sources.indexOf(source) === index
  );

// Aceleres 2026-08-18: a MINIATURA pede a miniatura, nao o arquivo original.
//
// A lista acima serve ao palco, onde `data_url` (o arquivo inteiro) tem de vir
// primeiro. A tira do rodape usava a mesma ordem, e por isso cada miniatura
// baixava a foto em tamanho real: numa conversa com muitas fotos sao dezenas de
// downloads completos, e os que falham por peso ou por limite de pedidos
// apareciam como miniatura quebrada. O `thumb_url` do Active Storage tem
// poucos KB e e exatamente para isto.
//
// Video nao tem `thumb_url` (os previewers estao desligados, ver
// Attachment#thumb_url), entao ele cai sozinho no arquivo — que e de onde o
// <video> ja tira o primeiro quadro.
const thumbnailSourceCandidates = attachment =>
  [
    attachment?.thumb_url,
    attachment?.thumbUrl,
    attachment?.data_url,
    attachment?.dataUrl,
  ].filter(
    (source, index, sources) => source && sources.indexOf(source) === index
  );

const attachmentFileType = attachment =>
  attachment?.file_type || attachment?.fileType;
const isGalleryLottieSticker = attachment =>
  isLottieStickerAttachment(attachment) ||
  (isMobileViewport.value &&
    isStickerAttachment(attachment) &&
    String(attachment?.extension || '').toLowerCase() === 'json');

// Endereco EXATO que a miniatura pede — e a chave com que o resolvedor guarda
// o resultado. Tem de ser uma funcao so: consultar o estado por uma URL e pedir
// o download por outra deixa a celula em espera para sempre, porque a falha fica
// registada numa chave que ninguem le.
//
// A imagem pede `?variant=thumb` (poucos KB). No mobile, o vídeo aberto
// reutiliza a URL que já chegou com a mensagem; os demais seguem pela lista.
const thumbnailRequestUrl = attachment => {
  const isOpenedMobileVideo =
    isMobileViewport.value &&
    [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
      attachmentFileType(attachment)
    ) &&
    isSameAttachment(attachment, props.attachment);
  const url =
    (isOpenedMobileVideo
      ? thumbnailSourceCandidates(props.attachment)[0]
      : '') ||
    thumbnailSourceCandidates(attachment)[0] ||
    '';
  if (!url) return '';

  const wantsThumbVariant =
    attachmentFileType(attachment) === ALLOWED_FILE_TYPES.IMAGE &&
    !isGalleryLottieSticker(attachment);

  return wantsThumbVariant ? withThumbVariant(url) : url;
};

const thumbnailSource = attachment =>
  guardedMediaSrc(thumbnailRequestUrl(attachment));

const galleryAttachments = computed(() => {
  if (
    isMobileViewport.value &&
    (props.isSticker || isStickerAttachment(props.attachment))
  ) {
    return [props.attachment];
  }

  const sourceAttachments =
    isMobileViewport.value && props.mobileAttachments.length
      ? props.mobileAttachments
      : props.allAttachments;
  const openedType = attachmentFileType(props.attachment);
  const allowedTypes =
    openedType === ALLOWED_FILE_TYPES.AUDIO
      ? [ALLOWED_FILE_TYPES.AUDIO]
      : [
          ALLOWED_FILE_TYPES.IMAGE,
          ALLOWED_FILE_TYPES.VIDEO,
          ALLOWED_FILE_TYPES.IG_REEL,
        ];
  const stickerMessageIds = new Set(
    isMobileViewport.value
      ? (getters.getSelectedChat.value?.messages || [])
          .filter(isStickerMessage)
          .map(message => String(message.id))
      : []
  );

  const attachments = sourceAttachments.filter(attachment => {
    const fileType = attachmentFileType(attachment);
    return (
      allowedTypes.includes(fileType) &&
      (!isMobileViewport.value ||
        (!isStickerAttachment(attachment) &&
          !stickerMessageIds.has(
            String(attachment.message_id || attachment.messageId)
          ))) &&
      mediaSourceCandidates(attachment).length
    );
  });
  const seenAttachmentKeys = new Set();
  const uniqueAttachments = attachments.filter(attachment => {
    const key = attachmentKey(attachment);
    if (seenAttachmentKeys.has(key)) return false;
    seenAttachmentKeys.add(key);
    return true;
  });

  if (
    mediaSourceCandidates(props.attachment).length &&
    !uniqueAttachments.some(attachment =>
      isSameAttachment(attachment, props.attachment)
    )
  ) {
    uniqueAttachments.push(props.attachment);
  }

  return uniqueAttachments.length ? uniqueAttachments : [props.attachment];
});

const initialAttachmentIndex = galleryAttachments.value.findIndex(attachment =>
  isSameAttachment(attachment, props.attachment)
);
const activeImageIndex = ref(Math.max(0, initialAttachmentIndex));

const imageRef = useTemplateRef('imageRef');
const imagePreviewRef = useTemplateRef('imagePreviewRef');
const gifRef = useTemplateRef('gifRef');
const galleryOverlayRef = useTemplateRef('galleryOverlayRef');
const galleryStageRef = useTemplateRef('galleryStageRef');
const mobilePhotoActionsRef = useTemplateRef('mobilePhotoActionsRef');
const mobileVideoActionsRef = useTemplateRef('mobileVideoActionsRef');
const mobileMediaActionsRef = useTemplateRef('mobileMediaActionsRef');
const mobileVideoCaptionTextRef = useTemplateRef('mobileVideoCaptionTextRef');
const mobileVideoCaptionExpanded = ref(false);
const mobileVideoCaptionOverflowing = ref(false);
const galleryTrackRef = useTemplateRef('galleryTrackRef');
const thumbnailTrackRef = useTemplateRef('thumbnailTrackRef');
const galleryTouchStart = ref(null);
const isActiveImageLoaded = ref(false);
const activeMediaFailed = ref(false);
const activeMediaAttempt = ref(0);

const {
  zoomScale,
  panOffset,
  isDragging,
  imageWrapperStyle,
  imageStyle,
  activeImageRotation,
  onZoom,
  setZoomScale,
  cancelPointerDrag,
  onDoubleClickZoomImage,
  onWheelImageZoom,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  resetZoomAndRotation,
} = useImageZoom(imageRef);

const onStickerDoubleClickZoom = event => {
  if (isMobileViewport.value && activeSticker.value) {
    event.preventDefault();
    return;
  }

  onDoubleClickZoomImage(event);
};

const onGalleryWheel = event => {
  if (isMobileViewport.value && activeSticker.value) return;
  onWheelImageZoom(event);
};

// Aceleres 2026-08-17: a foto de perfil do contato abre neste mesmo
// visualizador, mas ali nao existe mensagem por tras -- nenhuma das acoes do
// cabecalho (zoom, girar, baixar, responder, reagir) tem sobre o que agir, e o
// nome/horario repetem o que a ficha ao lado ja mostra. Neste modo o cabecalho
// fica so com o X, sobreposto a imagem, e as colunas de navegacao somem: a foto
// passa a ocupar a area inteira do visualizador. A marca vem do proprio anexo
// montado em ContactInfo.openAvatarViewer -- ver `aceleresContactAvatar` la.
const isContactAvatar = computed(() =>
  Boolean(activeAttachment.value?.aceleresContactAvatar)
);
const isMobileContactAvatar = computed(() =>
  Boolean(activeAttachment.value?.aceleresMobileContactAvatar)
);

const currentUser = computed(() => getters.getCurrentUser.value);
const currentAccountId = computed(() => getters.getCurrentAccountId.value);
const selectedChat = computed(() => getters.getSelectedChat.value);
const hasMoreThanOneAttachment = computed(
  () =>
    galleryAttachments.value.length > 1 &&
    (!isMobileViewport.value || !activeSticker.value)
);
const canShowMobileMediaOverview = computed(
  () =>
    isMobileViewport.value &&
    !activeSticker.value &&
    !props.attachment?.aceleresContactAvatar &&
    [
      ALLOWED_FILE_TYPES.IMAGE,
      ALLOWED_FILE_TYPES.VIDEO,
      ALLOWED_FILE_TYPES.IG_REEL,
    ].includes(attachmentFileType(props.attachment)) &&
    props.mobileOverviewAttachments.length > 1
);
const mobileMediaCountLabel = computed(() => {
  const total = props.mobileOverviewAttachments.length;
  return `${total} ${total === 1 ? 'mídia' : 'mídias'}`;
});

const mobileOverviewItems = computed(() =>
  props.mobileOverviewAttachments.map((attachment, index) => ({
    attachment,
    index,
  }))
);

const thumbnailMessageId = attachment =>
  attachment?.message_id || attachment?.messageId;
const starredMessageIds = computed(
  () =>
    new Set(
      (getters.getUISettings.value?.starred_message_ids || []).map(Number)
    )
);
const isThumbnailStarred = attachment =>
  starredMessageIds.value.has(Number(thumbnailMessageId(attachment)));
const isThumbnailPinned = attachment =>
  isMessagePinned(selectedChat.value, thumbnailMessageId(attachment));
const thumbnailLabel = (attachment, index) => {
  const labels = [`${index + 1} / ${galleryAttachments.value.length}`];
  if (isThumbnailStarred(attachment)) {
    labels.push(t('CONVERSATION.CONTEXT_MENU.STARRED'));
  }
  if (isThumbnailPinned(attachment)) {
    labels.push(t('CONVERSATION.CONTEXT_MENU.PINNED'));
  }
  return labels.join(', ');
};

const thumbnailItems = computed(() => {
  return galleryAttachments.value.map((attachment, index) => ({
    attachment,
    index,
  }));
});

const activeMessage = computed(() => {
  const messageId =
    activeAttachment.value?.message_id || activeAttachment.value?.messageId;
  if (!messageId) return null;

  return selectedChat.value?.messages?.find(
    message => String(message.id) === String(messageId)
  );
});
const activeSticker = computed(() => {
  if (!isMobileViewport.value) return props.isSticker;
  return (
    isStickerAttachment(activeAttachment.value) ||
    isStickerMessage(activeMessage.value) ||
    (props.isSticker &&
      isSameAttachment(activeAttachment.value, props.attachment))
  );
});
const reactionChannelType = computed(() => {
  const inboxId = activeMessage.value?.inbox_id ?? selectedChat.value?.inbox_id;
  const getInbox = store.getters['inboxes/getInbox'];
  return inboxId && typeof getInbox === 'function'
    ? getInbox(inboxId)?.channel_type || ''
    : '';
});

// Aceleres 2026-08-19: a reacao ja dada aparece TAMBEM aqui dentro. O visor
// tinha o botao de reagir, mas nao mostrava o que ja existia: abrir a foto
// escondia a pilula que estava na conversa. Vem do mesmo componente da bolha
// (MessageReactions), para a pilula ser a mesma coisa nos dois sitios.
const activeReactions = computed(() => {
  const reactions = activeMessage.value?.content_attributes?.reactions;

  return Array.isArray(reactions) ? reactions : [];
});

const activeMessageId = computed(
  () =>
    activeAttachment.value?.message_id ||
    activeAttachment.value?.messageId ||
    activeMessage.value?.id ||
    null
);

const activeMessagePayload = computed(() => {
  const message = activeMessage.value || {};
  return {
    ...message,
    id: message.id || activeMessageId.value,
    conversation_id: message.conversation_id || selectedChat.value?.id,
    attachments:
      message.attachments?.length > 0
        ? message.attachments
        : [activeAttachment.value],
    status: message.status || 'sent',
    created_at:
      message.created_at || activeAttachment.value?.created_at || null,
  };
});

// `favorite_stickers` é apenas a origem legada. A biblioteca canônica vive no
// Laravel e identifica a mídia pelo conteúdo; assim usuário e instância podem
// ter vínculos distintos apontando para o mesmo arquivo físico.
const { uiSettings, updateUISettings } = useUISettings();

const favoriteStickers = computed(
  () => uiSettings.value?.favorite_stickers || []
);

const stickerUrl = computed(
  () =>
    activeAttachment.value?.data_url || activeAttachment.value?.dataUrl || ''
);

const savingStickerScope = ref('');
const savedStickerScopes = ref([]);
const stickerLibraryId = ref(null);
const resolvingStickerLibrary = ref(false);
let stickerLibraryResolution = 0;

const stickerSavedFor = scope => savedStickerScopes.value.includes(scope);

const stickerConversationContext = () => {
  const accountId = currentAccountId.value;
  const conversationId = selectedChat.value?.id;
  const inboxId = selectedChat.value?.inbox_id;
  const accessToken = currentUser.value?.access_token || '';

  return accountId && conversationId && inboxId && accessToken
    ? { accountId, conversationId, inboxId, accessToken }
    : null;
};

const sha256Hex = async blob => {
  if (!window.crypto?.subtle) return '';
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    await blob.arrayBuffer()
  );
  return [...new Uint8Array(digest)]
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
};

const hydrateStickerLibraryState = async () => {
  stickerLibraryResolution += 1;
  const resolution = stickerLibraryResolution;
  savedStickerScopes.value = [];
  stickerLibraryId.value = null;
  if (!activeSticker.value || !stickerUrl.value) return;

  const context = stickerConversationContext();
  if (!context) return;

  resolvingStickerLibrary.value = true;
  try {
    const messageAttributes =
      activeMessagePayload.value?.content_attributes || {};
    const originHash = String(
      messageAttributes.aceleres_sticker_content_hash || ''
    );
    const originId = Number(messageAttributes.aceleres_sticker_library_id || 0);
    const contentHashPromise = originHash
      ? Promise.resolve(originHash)
      : fetch(stickerUrl.value, {
          credentials: 'same-origin',
          headers: { 'X-Chatwoot-Token': context.accessToken },
        }).then(response => {
          if (!response.ok) throw new Error('sticker_media_unavailable');
          return response.blob().then(sha256Hex);
        });
    const [contentHash, libraryResponse] = await Promise.all([
      contentHashPromise,
      fetch(
        `${ACELERES_API}/api/stickers?${new URLSearchParams({
          chatwoot_account_id: String(context.accountId),
          chatwoot_conversation_id: String(context.conversationId),
          chatwoot_inbox_id: String(context.inboxId),
        })}`,
        {
          headers: {
            Accept: 'application/json',
            'X-Chatwoot-Token': context.accessToken,
          },
        }
      ),
    ]);
    if (!libraryResponse.ok) return;

    const payload = await libraryResponse.json();
    if (resolution !== stickerLibraryResolution || !contentHash) return;

    const match = (payload?.results || []).find(
      sticker =>
        sticker.content_hash === contentHash || Number(sticker.id) === originId
    );
    if (!match) return;

    stickerLibraryId.value = Number(match.id) || null;
    savedStickerScopes.value = Array.isArray(match.scopes)
      ? match.scopes
      : [match.scope].filter(Boolean);
  } catch {
    // A mídia continua utilizável no visor. A ação de salvar fará a validação
    // completa e mostrará o erro específico se a rede ainda estiver instável.
  } finally {
    if (resolution === stickerLibraryResolution) {
      resolvingStickerLibrary.value = false;
    }
  }
};

const saveStickerToLibrary = async scope => {
  if (
    savingStickerScope.value ||
    resolvingStickerLibrary.value ||
    stickerSavedFor(scope) ||
    !stickerUrl.value
  )
    return;

  const context = stickerConversationContext();
  if (!context) {
    useAlert('Não foi possível identificar a conversa atual.');
    return;
  }

  savingStickerScope.value = scope;
  try {
    const formData = new FormData();
    if (stickerLibraryId.value) {
      formData.append('source_gif_id', String(stickerLibraryId.value));
    } else {
      const mediaResponse = await fetch(stickerUrl.value, {
        credentials: 'same-origin',
        headers: { 'X-Chatwoot-Token': context.accessToken },
      });
      if (!mediaResponse.ok) {
        throw new Error('Não foi possível ler esta figurinha.');
      }

      const blob = await mediaResponse.blob();
      const contentType = blob.type || 'image/webp';
      const extension = (contentType.split('/')[1] || 'webp').replace(
        /[^a-z0-9]/gi,
        ''
      );
      formData.append(
        'gif',
        new File([blob], `figurinha.${extension}`, { type: contentType })
      );
    }
    formData.append('scope', scope);
    formData.append('name', 'Figurinha guardada');
    formData.append('chatwoot_account_id', String(context.accountId));
    formData.append('chatwoot_conversation_id', String(context.conversationId));
    formData.append('chatwoot_inbox_id', String(context.inboxId));

    const response = await fetch(`${ACELERES_API}/api/stickers`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Chatwoot-Token': context.accessToken,
      },
      body: formData,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(
        payload?.message || 'Não foi possível guardar a figurinha.'
      );
    }

    savedStickerScopes.value = [
      ...new Set([...savedStickerScopes.value, scope]),
    ];
    stickerLibraryId.value =
      Number(payload?.data?.id) || stickerLibraryId.value;
    // Remove o apontamento legado por URL depois que a biblioteca canônica,
    // baseada no hash do arquivo, confirmou a persistência.
    updateUISettings({
      favorite_stickers: favoriteStickers.value.filter(
        item => item.url !== stickerUrl.value
      ),
    });
    useAlert(
      scope === 'instance'
        ? 'Figurinha adicionada para esta instância.'
        : 'Figurinha adicionada só para você.'
    );
  } catch (error) {
    useAlert(error?.message || 'Não foi possível guardar a figurinha.');
  } finally {
    savingStickerScope.value = '';
  }
};

watch(
  [activeMessageId, stickerUrl, activeSticker],
  hydrateStickerLibraryState,
  { immediate: true }
);

const isStarred = computed(() =>
  starredMessageIds.value.has(Number(activeMessageId.value))
);

const isPinned = computed(() =>
  isMessagePinned(selectedChat.value, activeMessageId.value)
);

const readableTime = computed(() => {
  const createdAt =
    activeAttachment.value?.created_at || activeMessage.value?.created_at;
  if (!createdAt) return '';

  const numericTimestamp = Number(createdAt);
  const date = Number.isFinite(numericTimestamp)
    ? new Date(
        numericTimestamp < 1_000_000_000_000
          ? numericTimestamp * 1000
          : numericTimestamp
      )
    : new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const dayDifference = Math.round(
    (startOfDate.getTime() - startOfToday.getTime()) / 86_400_000
  );
  const userLocale = String(locale.value || 'pt_BR').replace('_', '-');
  const time = new Intl.DateTimeFormat(userLocale, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  const dateLabel = [-1, 0].includes(dayDifference)
    ? new Intl.RelativeTimeFormat(userLocale, { numeric: 'auto' }).format(
        dayDifference,
        'day'
      )
    : new Intl.DateTimeFormat(userLocale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
  const normalizedDateLabel = `${dateLabel.charAt(0).toUpperCase()}${dateLabel.slice(1)}`;
  let atLabel = 'at';
  if (userLocale.startsWith('pt')) atLabel = 'às';
  else if (userLocale.startsWith('es')) atLabel = 'a las';

  return `${normalizedDateLabel} ${atLabel} ${time}`;
});

const mobileOverviewMediaTime = attachment => {
  const createdAt = attachment?.created_at || attachment?.createdAt;
  if (!createdAt) return '';

  const numericTimestamp = Number(createdAt);
  const date = Number.isFinite(numericTimestamp)
    ? new Date(
        numericTimestamp < 1_000_000_000_000
          ? numericTimestamp * 1000
          : numericTimestamp
      )
    : new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(
    String(locale.value || 'pt_BR').replace('_', '-'),
    {
      hour: '2-digit',
      minute: '2-digit',
    }
  ).format(date);
};

const mobileOverviewAttachmentLabel = index =>
  `Abrir mídia ${index + 1} de ${props.mobileOverviewAttachments.length}`;

// Aceleres 2026-08-17: a figurinha VETORIAL (Lottie) e um .json — nao ha
// imagem para o `<img>` mostrar. Ate aqui ela nem chegava a este visor: a
// bolha dela nao tinha clique nenhum, e por isso "alguns stickers nao abriam".
// O reconhecimento e o MESMO usado pela bolha e pela citacao, para uma
// figurinha nao ser vetorial num sitio e nao noutro.
const isLottieSticker = computed(() =>
  isGalleryLottieSticker(activeAttachment.value)
);

// A figurinha aberta usa uma caixa própria de 470px. Em telas menores, os dois
// eixos encolhem juntos para preservar o quadrado e manter o botão visível.
const stickerViewerSize =
  'min(470px, calc(100vw - 2rem), calc(100dvh - 12rem))';
const stickerBoxStyle = {
  width: stickerViewerSize,
  height: stickerViewerSize,
};

const gifChatSizeStyle = computed(() => {
  const { width, height } = activeAttachment.value || {};
  const ratio =
    Number(width) > 0 && Number(height) > 0
      ? Number(width) / Number(height)
      : null;

  // Sem proporcao conhecida nao se encolhe nada: o GIF ocupa o palco como
  // antes. Melhor isso do que inventar uma medida que a bolha nao usa.
  return gifFrameStyle(ratio) || {};
});

const isImage = computed(
  () =>
    activeFileType.value === ALLOWED_FILE_TYPES.IMAGE && !isLottieSticker.value
);
// Aceleres: GIF do WhatsApp chega como MP4 mudo (mesmo file type de vídeo),
// mas na galeria ele não deve virar player de vídeo — sem controles, sem
// play/pause, tocando em loop infinito, do mesmo jeito que a bolha do chat
// já trata (pedido 2026-08-11). content_attributes prevalece.
//
// Aceleres 2026-08-18: a resposta passou a ser do anexo ATIVO, e não da
// abertura. Agora que o GIF divide a galeria com as fotos, dá para abrir um
// GIF e navegar até uma foto — e o `initialIsGif` fixo mantinha a foto seguinte
// desenhada como GIF (sem controles, em loop). Ele continua a valer, mas só
// enquanto o anexo ativo for aquele com que a galeria abriu.
const isGifMedia = computed(() => {
  const attributes = activeMessage.value?.content_attributes || {};
  const mediaType =
    attributes.wame_media_type ?? attributes.wameMediaType ?? '';
  if (mediaType) return String(mediaType).toLowerCase() === 'gif';

  if (isGifAttachment(activeAttachment.value)) return true;

  return (
    props.initialIsGif &&
    isSameAttachment(activeAttachment.value, props.attachment)
  );
});
const isImageZoomed = computed(
  () =>
    isImage.value &&
    !isGifMedia.value &&
    !activeSticker.value &&
    zoomScale.value > 1
);
const isImageHistoryHidden = computed(
  () => isImageZoomed.value && zoomScale.value >= 3
);
const TOOLBAR_MAX_IMAGE_ZOOM = 4;
const showsImageZoomActions = computed(
  () => isImage.value && !isGifMedia.value && !activeSticker.value
);
const canDecreaseImageZoom = computed(() => zoomScale.value > 1);
const canIncreaseImageZoom = computed(
  () => zoomScale.value < TOOLBAR_MAX_IMAGE_ZOOM
);
const decreaseImageZoom = () => onZoom(-1);
const increaseImageZoom = () => {
  if (canIncreaseImageZoom.value) onZoom(1);
};
const isVideo = computed(
  () =>
    [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
      activeFileType.value
    ) && !isGifMedia.value
);
const showsExtendedMediaActions = computed(
  () =>
    !isMobileContactAvatar.value &&
    !activeSticker.value &&
    (isImage.value || isVideo.value || isGifMedia.value)
);
const isAudio = computed(
  () => activeFileType.value === ALLOWED_FILE_TYPES.AUDIO
);

const MOBILE_MEDIA_MAX_ZOOM = 4;
const MOBILE_TAP_MOVE_TOLERANCE = 10;
let mobileTouchGesture = null;
let suppressNextMobileMediaClick = false;
let mobileClickSuppressionTimer = null;

const activeMobileZoomScale = computed(() =>
  isImage.value ? zoomScale.value : mobileMediaScale.value
);

const isMobileMediaZoomed = computed(() => activeMobileZoomScale.value > 1);

const mobileMediaTransformStyle = computed(() => {
  if (isImage.value || mobileMediaScale.value === 1) return {};

  return {
    transform: `scale(${mobileMediaScale.value})`,
    transformOrigin: 'center center',
  };
});

const setActiveMobileZoomScale = scale => {
  const nextScale = Math.max(1, Math.min(MOBILE_MEDIA_MAX_ZOOM, scale));
  if (isImage.value) {
    setZoomScale(nextScale);
    return;
  }

  mobileMediaScale.value = nextScale;
};

const touchDistance = touches => {
  const firstTouch = touches[0];
  const secondTouch = touches[1];
  return Math.hypot(
    secondTouch.clientX - firstTouch.clientX,
    secondTouch.clientY - firstTouch.clientY
  );
};

const startMobilePinch = event => {
  const distance = touchDistance(event.touches);
  if (!distance) return;

  event.preventDefault();
  galleryTouchStart.value = null;
  cancelPointerDrag();
  mobileTouchGesture = {
    pinching: true,
    moved: true,
    startDistance: distance,
    startScale: activeMobileZoomScale.value,
  };
};

const onMobileMediaTouchStart = event => {
  if (
    (!isMobileViewport.value && !isMobileContactAvatar.value) ||
    (!showsExtendedMediaActions.value && !isContactAvatar.value)
  ) {
    return;
  }

  if (event.touches.length === 2) {
    startMobilePinch(event);
    return;
  }

  if (event.touches.length !== 1) return;
  const touch = event.touches[0];
  mobileTouchGesture = {
    pinching: false,
    moved: false,
    startX: touch.clientX,
    startY: touch.clientY,
  };
};

const onMobileMediaTouchMove = event => {
  if (
    (!isMobileViewport.value && !isMobileContactAvatar.value) ||
    !mobileTouchGesture
  ) {
    return;
  }

  if (event.touches.length === 2) {
    if (!mobileTouchGesture.pinching) startMobilePinch(event);
    if (!mobileTouchGesture?.pinching) return;

    event.preventDefault();
    const scale =
      mobileTouchGesture.startScale *
      (touchDistance(event.touches) / mobileTouchGesture.startDistance);
    setActiveMobileZoomScale(scale);
    return;
  }

  if (event.touches.length !== 1 || mobileTouchGesture.pinching) return;
  const touch = event.touches[0];
  const deltaX = touch.clientX - mobileTouchGesture.startX;
  const deltaY = touch.clientY - mobileTouchGesture.startY;
  if (Math.hypot(deltaX, deltaY) > MOBILE_TAP_MOVE_TOLERANCE) {
    mobileTouchGesture.moved = true;
  }
};

const suppressMobileMediaClick = () => {
  suppressNextMobileMediaClick = true;
  window.clearTimeout(mobileClickSuppressionTimer);
  mobileClickSuppressionTimer = window.setTimeout(() => {
    suppressNextMobileMediaClick = false;
  }, 500);
};

const onMobileMediaTouchEnd = event => {
  if (
    (!isMobileViewport.value && !isMobileContactAvatar.value) ||
    !mobileTouchGesture
  ) {
    return;
  }

  if (
    isMobileContactAvatar.value &&
    !mobileTouchGesture.pinching &&
    event.touches.length === 0 &&
    event.changedTouches[0].clientY - mobileTouchGesture.startY > 100
  ) {
    emit('close');
  }

  if (mobileTouchGesture.pinching || mobileTouchGesture.moved) {
    suppressMobileMediaClick();
  }
  if (event.touches.length === 0) mobileTouchGesture = null;
};

const TECHNICAL_MEDIA_CAPTION_LABELS = new Set([
  'mensagem recebida',
  '[mensagem recebida]',
  'message received',
  '[message received]',
  'mídia recebida',
  '[mídia recebida]',
  'media received',
  '[media received]',
]);

const activeMediaCaption = computed(() => {
  const messageContent = String(activeMessage.value?.content || '').trim();
  const isInitialAttachment = isSameAttachment(
    activeAttachment.value,
    props.attachment
  );
  const fallbackContent = isInitialAttachment
    ? String(props.initialCaption || '').trim()
    : '';
  const value = messageContent || fallbackContent;

  if (!value || TECHNICAL_MEDIA_CAPTION_LABELS.has(value.toLowerCase())) {
    return '';
  }
  return value;
});

const measureMobileVideoCaption = () => {
  const caption = mobileVideoCaptionTextRef.value;
  mobileVideoCaptionOverflowing.value = Boolean(
    caption && caption.scrollWidth > caption.clientWidth
  );
};

useResizeObserver(mobileVideoCaptionTextRef, measureMobileVideoCaption);
watch([activeAttachment, activeMediaCaption], () => {
  mobileVideoCaptionExpanded.value = false;
  nextTick(measureMobileVideoCaption);
});

// A coluna e a ancora da foto só mudam quando existe legenda.
const isMobilePhoto = computed(
  () =>
    isMobileViewport.value &&
    isImage.value &&
    !isGifMedia.value &&
    !activeSticker.value &&
    !isContactAvatar.value &&
    Boolean(activeMediaCaption.value)
);
const mobileActionTarget = computed(() => {
  if (!isMobileViewport.value) return null;
  if (isMobilePhoto.value) return mobilePhotoActionsRef.value;
  return isVideo.value
    ? mobileVideoActionsRef.value
    : mobileMediaActionsRef.value;
});

// No telemovel, a pergunta que acompanha um reel precisa continuar ligada ao
// video sem ficar desenhada por cima dele. Outras midias mantem o overlay
// atual; o recorte e deliberadamente restrito ao anexo `ig_reel` com texto.
const isInstagramQuestionVideo = computed(
  () =>
    activeFileType.value === ALLOWED_FILE_TYPES.IG_REEL &&
    Boolean(activeMediaCaption.value)
);
const captionPlacement = computed(() =>
  getGalleryCaptionPlacement({
    hasCaption: Boolean(activeMediaCaption.value),
    isMobileViewport: isMobileViewport.value,
    hasExtendedMediaActions: showsExtendedMediaActions.value,
    isInstagramQuestionVideo: isInstagramQuestionVideo.value,
    isImage: isImage.value,
    isVideo: isVideo.value,
    isGifMedia: isGifMedia.value,
    isSticker: activeSticker.value,
    isImageZoomed: isImageZoomed.value,
  })
);

const videoProgressPercent = computed(() =>
  videoDuration.value ? (videoCurrentTime.value / videoDuration.value) * 100 : 0
);
const videoSpeedLabel = computed(
  () => `${videoPlaybackRate.value.toFixed(1)}x`
);
const formatVideoTime = seconds => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};
const videoCurrentTimeLabel = computed(() =>
  formatVideoTime(videoCurrentTime.value)
);
const videoDurationLabel = computed(() => formatVideoTime(videoDuration.value));
const mobileVideoRemainingTimeLabel = computed(
  () =>
    `-${formatVideoTime(
      Math.max(videoDuration.value - videoCurrentTime.value, 0)
    )}`
);
const mobileVideoSpeedLabel = computed(() => `${videoPlaybackRate.value}x`);
const volumeIconClass = computed(() => {
  if (isVideoMuted.value || videoVolume.value === 0) return 'i-lucide-volume-x';
  if (videoVolume.value < 0.5) return 'i-lucide-volume-1';
  return 'i-lucide-volume-2';
});

const clearVideoControlsHideTimer = () => {
  if (videoControlsHideTimer) {
    clearTimeout(videoControlsHideTimer);
    videoControlsHideTimer = null;
  }
};

const scheduleVideoControlsHide = () => {
  clearVideoControlsHideTimer();
  if (!isVideoPlaying.value || isVideoPointerHovering.value) return;
  videoControlsHideTimer = setTimeout(() => {
    showVideoControls.value = false;
  }, 3000);
};

const showControlsTemporarily = () => {
  showVideoControls.value = true;
  scheduleVideoControlsHide();
};

const onVideoMediaTouchStart = event => {
  showControlsTemporarily();
  onMobileMediaTouchStart(event);
};

const showControlsOnPointerEnter = event => {
  if (event.pointerType === 'touch') return;
  isVideoPointerHovering.value = true;
  clearVideoControlsHideTimer();
  showVideoControls.value = true;
};

const hideControlsOnPointerLeave = event => {
  if (event.pointerType === 'touch') return;
  isVideoPointerHovering.value = false;
  clearVideoControlsHideTimer();
  showVideoControls.value = false;
};

const resetVideoPlayerState = () => {
  isVideoPlaying.value = false;
  videoCurrentTime.value = 0;
  videoDuration.value = 0;
  isSeekingVideo.value = false;
  showVideoControls.value = false;
  isVideoPointerHovering.value = false;
  videoAspectRatio.value = '16 / 9';
  clearVideoControlsHideTimer();
};

const attemptVideoAutoplay = async () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  try {
    await videoEl.play();
  } catch (error) {
    videoEl.muted = true;
    isVideoMuted.value = true;
    try {
      await videoEl.play();
    } catch (mutedError) {
      // Reprodução automática bloqueada pelo navegador; usuário inicia manualmente.
    }
  }
};

const togglePlayPauseVideo = () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  if (videoEl.paused || videoEl.ended) {
    videoEl.play();
  } else {
    videoEl.pause();
  }
};

const onVideoLoadedMetadata = () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  videoDuration.value = videoEl.duration || 0;
  videoEl.volume = videoVolume.value;
  videoEl.playbackRate = videoPlaybackRate.value;
  if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
    videoAspectRatio.value = `${videoEl.videoWidth} / ${videoEl.videoHeight}`;
  }
  attemptVideoAutoplay();
};

const onVideoTimeUpdate = () => {
  if (isSeekingVideo.value || !videoRef.value) return;
  videoCurrentTime.value = videoRef.value.currentTime;
};

const onVideoPlaying = () => {
  isVideoPlaying.value = true;
  scheduleVideoControlsHide();
};

const onVideoPause = () => {
  isVideoPlaying.value = false;
  clearVideoControlsHideTimer();
};

const onVideoEnded = () => {
  isVideoPlaying.value = false;
  clearVideoControlsHideTimer();
};

const onSeekInput = event => {
  const value = Number(event.target.value);
  videoCurrentTime.value = value;
  if (videoRef.value) videoRef.value.currentTime = value;
};

const onSeekCommit = () => {
  isSeekingVideo.value = false;
  showControlsTemporarily();
};

const toggleMuteVideo = () => {
  const videoEl = videoRef.value;
  if (!videoEl) return;
  isVideoMuted.value = !isVideoMuted.value;
  videoEl.muted = isVideoMuted.value;
  showControlsTemporarily();
};

const onVolumeInput = event => {
  const value = Number(event.target.value);
  videoVolume.value = value;
  isVideoMuted.value = value === 0;
  const videoEl = videoRef.value;
  if (videoEl) {
    videoEl.volume = value;
    videoEl.muted = value === 0;
  }
  showControlsTemporarily();
};

const cyclePlaybackRate = () => {
  const currentIndex = PLAYBACK_RATE_STEPS.indexOf(videoPlaybackRate.value);
  const nextRate =
    PLAYBACK_RATE_STEPS[(currentIndex + 1) % PLAYBACK_RATE_STEPS.length];
  videoPlaybackRate.value = nextRate;
  if (videoRef.value) videoRef.value.playbackRate = nextRate;
  showControlsTemporarily();
};

const toggleVideoFullscreen = async () => {
  const wrapperEl = videoWrapperRef.value;
  const videoEl = videoRef.value;
  if (!wrapperEl || !videoEl) return;
  try {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else if (wrapperEl.requestFullscreen) {
      await wrapperEl.requestFullscreen();
    } else if (videoEl.webkitEnterFullscreen) {
      videoEl.webkitEnterFullscreen();
    }
  } catch (error) {
    // Tela cheia indisponível neste navegador.
  }
  showControlsTemporarily();
};

const onFullscreenChange = () => {
  isVideoFullscreen.value = Boolean(document.fullscreenElement);
};

const senderDetails = computed(() => {
  const {
    name,
    available_name: availableName,
    avatar_url,
    thumbnail,
    id,
  } = activeAttachment.value?.sender ||
  activeMessage.value?.sender ||
  props.attachment?.sender ||
  {};
  const isCurrentUser = id && String(currentUser.value?.id) === String(id);

  return {
    name: isCurrentUser
      ? t('CHAT_LIST.STARRED.YOU')
      : name || availableName || t('CHAT_LIST.STARRED.UNKNOWN_CONTACT'),
    avatar: thumbnail || avatar_url || '',
  };
});

const mobileOverviewTitle = computed(() => senderDetails.value.name);

// Aceleres 2026-08-17: enquanto a foto grande baixa, o quadro ja mostra a
// miniatura que a ficha do contato tem em cache — sem isto o visor abre num
// retangulo vazio e a foto "pisca" quando chega. E fundo, nao <img>: a imagem
// grande entra por cima assim que carrega, sem troca de elemento.
const avatarFallbackStyle = computed(() => {
  const thumb = senderDetails.value?.avatar;
  if (!isContactAvatar.value || !thumb) return {};

  // `contain` (e nao `cover`) para o fundo cair exatamente onde a foto grande
  // vai cair: sao a mesma proporcao no mesmo quadrado. Com `cover` a espera
  // aparecia recortada e "pulava" para o enquadramento certo ao carregar.
  return {
    backgroundImage: `url("${thumb}")`,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
});

// Aceleres 2026-08-20: o alvo é 700px VISÍVEIS. O painel Zoraxy costuma rodar
// com `zoom: 0.8`, portanto declarar 700px diretamente produzia só 560px na
// tela. O cálculo abaixo lê o zoom real e compensa tanto o teto quanto o limite
// do viewport, sem acoplar o visualizador a um valor fixo da instalação.
const AVATAR_VIEWER_TARGET_VISIBLE_SIZE = 700;
const AVATAR_VIEWER_MAX_VIEWPORT_PERCENT = 90;
const AVATAR_VIEWER_MIN_VISIBLE_SIZE = 240;

// Uma foto comum de perfil chega com 640px. Levá-la a 700px é uma ampliação de
// apenas 9,4%, pequena o suficiente para manter boa nitidez com a interpolação
// do navegador. Abaixo de 85% do alvo não se força a ampliação: uma foto de
// 80/96px fica limitada a 240px visíveis: grande o bastante para reconhecer a
// foto, mas sem ocupar o palco inteiro com compressão e borrado ampliados.
const AVATAR_VIEWER_TARGET_QUALITY_RATIO = 0.85;

const avatarViewerPageZoom = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 1;
  }

  const zoom = Number.parseFloat(
    window.getComputedStyle(document.documentElement).zoom
  );
  return Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
};

// `naturalWidth/naturalHeight` so existem depois que a imagem carrega, entao a
// medida fica guardada junto da chave do anexo a que ela pertence — assim o
// tamanho de uma foto nunca vaza para a proxima que abrir.
const avatarNaturalSize = ref({ key: '', size: 0 });

const onAvatarImageLoad = event => {
  if (!isContactAvatar.value) return;

  const element = event?.currentTarget;
  avatarNaturalSize.value = {
    key: attachmentKey(activeAttachment.value),
    size: Math.max(element?.naturalWidth || 0, element?.naturalHeight || 0),
  };
};

// Lado do quadrado onde a foto é desenhada. Fotos com resolução próxima do
// alvo ocupam cerca de 700px visíveis; as menores ficam no tamanho natural.
// Todos os valores são convertidos para o espaço de layout antes do `zoom` do
// documento, e `object-fit: contain` continua preservando a imagem inteira.
const avatarBoxStyle = computed(() => {
  if (!isContactAvatar.value) return {};

  const { key, size } = avatarNaturalSize.value;
  const natural = key === attachmentKey(activeAttachment.value) ? size : 0;
  const pageZoom = avatarViewerPageZoom();
  const targetLayoutSize = AVATAR_VIEWER_TARGET_VISIBLE_SIZE / pageZoom;
  const viewportLayoutPercent = AVATAR_VIEWER_MAX_VIEWPORT_PERCENT / pageZoom;
  const hasEnoughQuality =
    natural >=
    AVATAR_VIEWER_TARGET_VISIBLE_SIZE * AVATAR_VIEWER_TARGET_QUALITY_RATIO;
  const qualityVisibleSize = hasEnoughQuality
    ? AVATAR_VIEWER_TARGET_VISIBLE_SIZE
    : natural;
  const qualityLayoutSize =
    Math.max(AVATAR_VIEWER_MIN_VISIBLE_SIZE, qualityVisibleSize) / pageZoom;
  const side = natural
    ? `min(${viewportLayoutPercent}vmin, ${Math.min(
        targetLayoutSize,
        qualityLayoutSize
      )}px)`
    : `min(${viewportLayoutPercent}vmin, ${targetLayoutSize}px)`;

  return { width: side, height: side };
});

// A reação do visor pertence à mídia, não à largura do painel. A posição sai
// do retângulo realmente desenhado para continuar certa em foto vertical,
// GIF, vídeo, rotação, zoom e redimensionamento da janela. A pílula fica
// inteira no canto inferior direito da mídia para a legenda nunca recortá-la.
const mediaReactionStyle = ref(null);
const mediaFrameStyle = ref(null);
const mobilePhotoDetailsStyle = computed(() => {
  const frame = mediaFrameStyle.value;
  if (!frame) return undefined;
  if (isImageZoomed.value) return frame;

  return { width: frame.width, marginLeft: frame.left };
});
const mobilePhotoActionStyle = ref(null);
const mobileMediaActionStyle = ref(null);

const activeReactionAnchor = () => {
  if (activeSticker.value || isContactAvatar.value) return null;
  if (isGifMedia.value && gifRef.value) return gifRef.value;
  if (isVideo.value) return videoWrapperRef.value;
  if (isImage.value) {
    return isActiveImageLoaded.value
      ? imageRef.value
      : imagePreviewRef.value || imageRef.value;
  }
  return null;
};

const updateMediaReactionPosition = () => {
  const overlay = galleryOverlayRef.value;
  const media = activeReactionAnchor();
  const stage = galleryStageRef.value;
  if (!overlay || !media) {
    mediaReactionStyle.value = null;
    mediaFrameStyle.value = null;
    mobilePhotoActionStyle.value = null;
    mobileMediaActionStyle.value = null;
    return;
  }

  const overlayRect = overlay.getBoundingClientRect();
  const mediaRect = media.getBoundingClientRect();
  const stageRect = stage?.getBoundingClientRect();
  const pageZoom = avatarViewerPageZoom();
  const { rect: chromeRect, style: frameStyle } = getGalleryMediaFrame({
    mediaRect,
    overlayRect,
    stageRect,
    isMobileViewport: isMobileViewport.value,
    isMobileMediaZoomed: isMobileMediaZoomed.value,
    isMobilePhoto: isMobilePhoto.value,
    pageZoom,
  });
  mediaFrameStyle.value = frameStyle;
  mobilePhotoActionStyle.value = isMobilePhoto.value
    ? getGalleryMediaActionStyle({
        mediaRect,
        stageRect: stageRect || mediaRect,
        pageZoom,
      })
    : null;

  if (isVideo.value && stage) {
    const actionWidth = chromeRect.width / pageZoom;
    mobileMediaActionStyle.value = {
      left: `${(chromeRect.left - stageRect.left) / pageZoom}px`,
      top: `${(chromeRect.top - stageRect.top) / pageZoom}px`,
      width: `${actionWidth}px`,
      height: `${chromeRect.height / pageZoom}px`,
    };
  } else {
    mobileMediaActionStyle.value = null;
  }

  if (!activeReactions.value.length) {
    mediaReactionStyle.value = null;
    return;
  }

  const mediaInset = 8;
  const reactionBottom = Math.max(
    0,
    mediaRect.bottom - overlayRect.top - mediaInset
  );
  const reactionRight = Math.max(
    0,
    overlayRect.right - mediaRect.right + mediaInset
  );
  mediaReactionStyle.value = {
    right: `${reactionRight / pageZoom}px`,
    top: `${reactionBottom / pageZoom}px`,
  };
};

const onActiveImageLoad = event => {
  isActiveImageLoaded.value = true;
  onAvatarImageLoad(event);
  nextTick(updateMediaReactionPosition);
};

const onActiveImagePreviewLoad = () => {
  nextTick(updateMediaReactionPosition);
};

useResizeObserver(galleryOverlayRef, updateMediaReactionPosition);
useResizeObserver(galleryStageRef, updateMediaReactionPosition);
useResizeObserver(imageRef, updateMediaReactionPosition);
useResizeObserver(imagePreviewRef, updateMediaReactionPosition);
useResizeObserver(gifRef, updateMediaReactionPosition);
useResizeObserver(videoWrapperRef, updateMediaReactionPosition);

watch(
  [
    activeAttachment,
    activeReactions,
    isImage,
    isGifMedia,
    isVideo,
    zoomScale,
    mobileMediaScale,
    activeImageRotation,
  ],
  () => {
    mediaReactionStyle.value = null;
    mediaFrameStyle.value = null;
    nextTick(updateMediaReactionPosition);
  },
  { flush: 'post' }
);

watch(
  panOffset,
  () => {
    nextTick(updateMediaReactionPosition);
  },
  { flush: 'post' }
);

const activeMediaSource = computed(
  () => mediaSourceCandidates(activeAttachment.value)[0] || ''
);

// O que vai no `src`: identico a URL crua quando ela abre sozinha, e o object
// URL baixado quando e do endpoint guardado. Fica separado de
// `activeMediaSource` porque baixar e compartilhar continuam a precisar do
// endereco real, nao do object URL local.
const activeMediaDisplaySource = computed(() => {
  const resolved = guardedMediaSrc(activeMediaSource.value);
  if (resolved) return resolved;
  if (activeMediaFailed.value || guardedMediaFailed(activeMediaSource.value)) {
    return thumbnailSource(activeAttachment.value) || '';
  }
  return '';
});
const isActiveMediaUnavailable = computed(() => {
  const sourceFailed =
    activeMediaFailed.value ||
    (Boolean(activeMediaSource.value) &&
      guardedMediaFailed(activeMediaSource.value));
  if (!sourceFailed) return false;
  return !activeMediaDisplaySource.value;
});
const activeMediaRenderKey = computed(
  () => `${attachmentKey(activeAttachment.value)}-${activeMediaAttempt.value}`
);

// Aceleres 27/08/2026: o audio tem endereco proprio. O WebKit do iPhone nao
// decodifica Ogg/Opus, que e o formato de praticamente todo audio da WAME, e o
// backend passou a guardar uma derivada AAC/MP4 ao lado do original
// (Aceleres::AudioCompatService). Duas formas de anexo chegam aqui: a que vem
// da MENSAGEM ja traz `playback_url` pronta; a que vem da lista de midias da
// ficha do contato chega pelo endpoint guardado, e la a derivada e pedida por
// `?variant=playback`. O download continua entregando o original.
const activeAudioRawSource = computed(() => {
  const attachment = activeAttachment.value;
  if (!attachment) return '';

  const source = activeMediaSource.value;
  if (isGuardedMediaUrl(source)) return withPlaybackVariant(source);

  return audioPlaybackSource(attachment) || source;
});

// O audio mantem o cache-busting que ja tinha, mas so onde ele faz sentido:
// colado num object URL, o parametro quebraria o endereco.
const activeAudioSource = computed(() => {
  const raw = activeAudioRawSource.value;
  if (!raw) return '';

  const source = guardedMediaSrc(raw);
  if (!source || isGuardedMediaUrl(raw)) return source;

  return `${source}?t=${Date.now()}`;
});

const isThumbnailUnavailable = attachment => {
  if (unavailableThumbnailKeys.value.has(attachmentKey(attachment))) {
    return true;
  }

  const url = thumbnailRequestUrl(attachment);
  return Boolean(url) && guardedMediaFailed(url);
};

// A miniatura ocupa o palco enquanto o arquivo original ainda chega. Alem de
// dar retorno visual imediato em redes moveis, ela fornece um retangulo real
// para as acoes de reagir e responder antes do carregamento terminar.
const activeImagePreviewSource = computed(() => {
  if (!isImage.value || isGifMedia.value || activeSticker.value) return '';

  return thumbnailSource(activeAttachment.value);
});

// A miniatura guardada comeca vazia (o download ainda corre) e so falha depois.
// Sem distinguir os dois casos, o placeholder de indisponivel piscava em todas
// as fotos do historico enquanto elas carregavam.
const isThumbnailPending = attachment => {
  const url = thumbnailRequestUrl(attachment);
  if (!url || !isGuardedMediaUrl(url)) return false;

  return !thumbnailSource(attachment) && !guardedMediaFailed(url);
};

const closeToolbarPopovers = () => {
  showMobileMenu.value = false;
  showReactionPicker.value = false;
  showFullEmojiPicker.value = false;
};

const toggleMobileChrome = () => {
  isMobileChromeVisible.value = !isMobileChromeVisible.value;
  if (!isMobileChromeVisible.value) {
    closeToolbarPopovers();
    if (!isVideo.value) showVideoControls.value = false;
  }
};

const onMediaSurfaceClick = () => {
  if (isContactAvatar.value || activeSticker.value) return;

  if (!isMobileViewport.value) {
    if (isVideo.value) togglePlayPauseVideo();
    return;
  }

  if (suppressNextMobileMediaClick) {
    suppressNextMobileMediaClick = false;
    window.clearTimeout(mobileClickSuppressionTimer);
    return;
  }

  // Aceleres 2026-09-14 (pedido do Facundo): no telemovel o GIF nao esconde a
  // barra ao toque. O GIF nao tem controlos para destapar, e sem a barra a
  // tela ficava so com o GIF — medido num grupo: tocar onde estava o
  // "Voltar" nao a trazia de volta, e nao havia como regressar a conversa.
  if (isGifMedia.value) return;

  toggleMobileChrome();
};

const onClose = () => {
  closeToolbarPopovers();
  showForwardModal.value = false;
  emit('close');
};

const onOverlayClick = () => {
  if (isMobileViewport.value) return;

  onClose();
};

const closeMediaViewer = () => onClose();

watch(
  show,
  isOpen => {
    if (isOpen) {
      showMobileMediaOverview.value = canShowMobileMediaOverview.value;
      isMobileChromeVisible.value = true;
      mobileMediaScale.value = 1;
      registerMediaViewer(closeMediaViewer);
    } else {
      unregisterMediaViewer(closeMediaViewer);
      stopReplyingHere();
    }
  },
  { immediate: true }
);

watch(isMobileViewport, isMobile => {
  if (!isMobile) showMobileMediaOverview.value = false;
  nextTick(updateMediaReactionPosition);
});

onBeforeUnmount(() => {
  releaseGuardedMedia();
  unregisterMediaViewer(closeMediaViewer);
  clearVideoControlsHideTimer();
  window.clearTimeout(mobileClickSuppressionTimer);
  document.removeEventListener('fullscreenchange', onFullscreenChange);
  if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
});

// Aceleres 2026-08-18: a figurinha VETORIAL (Lottie) chega como
// `application/json`, e o Rails guarda isso no anexo como file_type `file`
// (Attachment enum) — que nao esta na lista acima. Sem a excecao, este guarda
// devolvia cedo e `activeAttachment` ficava vazio: o palco abria em branco e o
// cabecalho caia no rotulo de contacto desconhecido ("Contato"), que e o
// "figurinha nao aparece e mostra um contacto estranho" relatado. Os dois
// sintomas sao o mesmo bug — o visor abria sem anexo nenhum.
//
// Entra como IMAGE de proposito: quem desenha e o ramo `isLottieSticker`, que
// ja tem precedencia sobre `isImage` (ver essas computeds), e assim as
// miniaturas e a navegacao continuam a tratar a figurinha como as outras.
const setImageAndVideoSrc = attachment => {
  const type = attachmentFileType(attachment);
  const isLottie = isLottieStickerAttachment(attachment);
  if (!isLottie && !Object.values(ALLOWED_FILE_TYPES).includes(type)) return;

  resetVideoPlayerState();
  isActiveImageLoaded.value = false;
  activeMediaFailed.value = false;
  activeAttachment.value = attachment;
  activeFileType.value = isLottie ? ALLOWED_FILE_TYPES.IMAGE : type;
};

const centerActiveThumbnail = behavior => {
  nextTick(() => {
    if (!isMobileViewport.value) {
      thumbnailTrackRef.value
        ?.querySelector('[data-gallery-thumbnail-active="true"]')
        ?.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
      return;
    }

    window.requestAnimationFrame(() => {
      const track = thumbnailTrackRef.value;
      const activeThumbnail = track?.querySelector(
        '[data-gallery-thumbnail-active="true"]'
      );
      if (!track || !activeThumbnail) return;

      const trackRect = track.getBoundingClientRect();
      const thumbnailRect = activeThumbnail.getBoundingClientRect();
      const left =
        track.scrollLeft +
        thumbnailRect.left +
        thumbnailRect.width / 2 -
        trackRect.left -
        trackRect.width / 2;
      track.scrollTo({ left, behavior });
    });
  });
};

watch(
  thumbnailTrackRef,
  track => {
    if (track && show.value && isMobileViewport.value) {
      centerActiveThumbnail('auto');
    }
  },
  { flush: 'post' }
);

watch(galleryAttachments, () => {
  if (!isMobileViewport.value) return;

  const index = galleryAttachments.value.findIndex(attachment =>
    isSameAttachment(attachment, activeAttachment.value)
  );
  if (index < 0) return;

  activeImageIndex.value = index;
  if (show.value && !showMobileMediaOverview.value) {
    centerActiveThumbnail('auto');
  }
});

const onThumbnailError = (event, attachment) => {
  const mediaElement = event.currentTarget;
  const candidates = [
    thumbnailRequestUrl(attachment),
    ...thumbnailSourceCandidates(attachment),
  ].filter(
    (source, index, sources) => source && sources.indexOf(source) === index
  );
  const nextSourceIndex = Number(mediaElement?.dataset?.sourceIndex || 0) + 1;

  if (mediaElement && candidates[nextSourceIndex]) {
    mediaElement.dataset.sourceIndex = String(nextSourceIndex);
    mediaElement.src = candidates[nextSourceIndex];
    return;
  }

  // Aceleres 2026-08-18: a miniatura que falha vira PLACEHOLDER, e o anexo
  // continua na tira.
  //
  // Antes ele era removido de `galleryAttachments`, e era isso que fazia as
  // fotos do historico "irem desaparecendo": basta uma falha passageira ao
  // buscar a miniatura (a variante do Active Storage ainda por gerar, um 401
  // ou um limite de pedidos com muitas miniaturas de uma vez) para o item sair
  // da tira e nunca mais voltar — mesmo com a midia em si intacta. Removendo
  // ainda por cima renumerava tudo e fazia a navegacao saltar.
  //
  // O desenho de indisponivel ja existia (ver `isThumbnailUnavailable` no
  // rodape), mas era codigo morto: o item era retirado antes de chegar a ser
  // desenhado. Agora ele e o que o utilizador ve, a posicao mantem-se e o
  // anexo continua clicavel — a midia grande pode abrir bem mesmo quando so a
  // miniatura falhou.
  unavailableThumbnailKeys.value = new Set([
    ...unavailableThumbnailKeys.value,
    attachmentKey(attachment),
  ]);
};

const onActiveMediaError = event => {
  const mediaElement = event.currentTarget;
  const candidates = mediaSourceCandidates(activeAttachment.value);
  const nextSourceIndex = Number(mediaElement?.dataset?.sourceIndex || 0) + 1;

  if (mediaElement && candidates[nextSourceIndex]) {
    mediaElement.dataset.sourceIndex = String(nextSourceIndex);
    mediaElement.src = candidates[nextSourceIndex];
    return;
  }

  activeMediaFailed.value = true;
  emit('error');
};

const retryActiveMedia = async () => {
  activeMediaFailed.value = false;
  isActiveImageLoaded.value = false;
  activeMediaAttempt.value += 1;

  const retryUrl = isAudio.value
    ? activeAudioRawSource.value
    : activeMediaSource.value;
  if (isGuardedMediaUrl(retryUrl)) {
    const resolved = await retryGuardedMedia(retryUrl);
    if (!resolved) activeMediaFailed.value = true;
  }
};

const onThumbnailWheel = event => {
  const track = thumbnailTrackRef.value;
  if (!track) return;

  const delta =
    Math.abs(event.deltaX) > Math.abs(event.deltaY)
      ? event.deltaX
      : event.deltaY;
  if (!delta) return;

  event.preventDefault();
  track.scrollLeft += delta;
};

const onClickChangeAttachment = (attachment, index) => {
  if (!attachment) return;

  closeToolbarPopovers();
  isMobileChromeVisible.value = true;
  mobileMediaScale.value = 1;
  mobileTouchGesture = null;
  activeImageIndex.value = index;
  setImageAndVideoSrc(attachment);
  resetZoomAndRotation();
  centerActiveThumbnail('smooth');
};

const openMobileOverviewAttachment = attachment => {
  const index = galleryAttachments.value.findIndex(item =>
    isSameAttachment(item, attachment)
  );
  if (index < 0) return;

  showMobileMediaOverview.value = false;
  onClickChangeAttachment(attachment, index);
};

const onMobileDetailBack = () => onClose();

const showPreviousAttachment = () => {
  onClickChangeAttachment(
    galleryAttachments.value[activeImageIndex.value - 1],
    activeImageIndex.value - 1
  );
};

const showNextAttachment = () => {
  onClickChangeAttachment(
    galleryAttachments.value[activeImageIndex.value + 1],
    activeImageIndex.value + 1
  );
};

// Aceleres 2026-09-09: no telemovel a troca de foto ACOMPANHA O DEDO.
//
// Ate aqui o gesto era so um atalho: `touchstart` guardava o ponto,
// `touchend` media 48px e trocava a foto de uma vez. Durante todo o arrasto
// nada se mexia — nao havia como saber se o gesto tinha sido entendido, nem o
// que vinha a seguir, e a foto nova aparecia de estalo. E o que se corrige
// aqui: a pista desloca-se com o dedo, a vizinha assoma pela borda, e ao
// largar a pista termina o percurso sozinha.
//
// Nada disto existe no computador: `isMobileViewport` fecha a porta logo a
// entrada, o deslocamento fica em 0 e a pista nem transicao tem.
const SWIPE_COMMIT_RATIO = 0.18;
const SWIPE_COMMIT_VELOCITY = 0.4;
const SWIPE_ANIMATION_MS = 260;
// A resistencia da ponta: na primeira e na ultima foto o arrasto continua a
// responder, mas so um terco, e volta ao lugar. E o mesmo sinal do WhatsApp —
// "o gesto foi lido, mas nao ha nada deste lado".
const SWIPE_EDGE_RESISTANCE = 0.32;

const swipeOffset = ref(0);
const swipeTransitionMs = ref(0);
let swipeGesture = null;
let swipeCommitTimer = null;
// Sentido de um percurso ja confirmado que ainda esta a correr ate a borda.
let pendingSwipeDirection = 0;

// A medida do percurso e a da PISTA mais os 8px de folga entre fotos — o
// mesmo numero que posiciona as vizinhas no CSS. Medir o palco (`main`) daria
// um valor diferente: ele inclui o recheio e, no computador, as duas colunas
// das setas; a foto seguinte pararia deslocada do centro.
const SWIPE_PEEK_GAP = 8;

const swipeStageWidth = () =>
  (galleryTrackRef.value?.clientWidth || window.innerWidth || 1) +
  SWIPE_PEEK_GAP;

// A vizinha entra pela MINIATURA, e nao pelo arquivo inteiro: ela ja esta em
// cache (e a mesma que a tira do rodape usa), pesa poucos KB e aparece no
// primeiro quadro do arrasto. O arquivo grande continua a ser pedido quando a
// foto passa de facto a ser a activa.
const peekAttachmentAt = offset =>
  isMobileViewport.value && hasMoreThanOneAttachment.value
    ? galleryAttachments.value[activeImageIndex.value + offset]
    : null;

const previousPeekAttachment = computed(() => peekAttachmentAt(-1));
const nextPeekAttachment = computed(() => peekAttachmentAt(1));

// Verdadeiro do primeiro pixel do arrasto ate a pista assentar. Serve para
// esconder a moldura escura que fica POR CIMA da foto (o gradiente do rodape,
// a legenda e as reaccoes): ela e posicionada pelo rectangulo da foto, medido
// com `getBoundingClientRect`, e vive FORA da pista — enquanto a foto desliza,
// aquela sombra fica onde estava e descola do gesto. No WhatsApp o mesmo
// acontece: ao arrastar, o que esta escrito sobre a foto desaparece e so volta
// quando a foto seguinte assenta.
const isSwipeInProgress = computed(() => swipeOffset.value !== 0);

const galleryTrackStyle = computed(() => ({
  transform: `translate3d(${swipeOffset.value}px, 0, 0)`,
  transition: swipeTransitionMs.value
    ? `transform ${swipeTransitionMs.value}ms cubic-bezier(0.22, 0.61, 0.36, 1)`
    : 'none',
  // Aceleres 2026-09-09: `will-change` SO enquanto a pista anda.
  //
  // Ele estava fixo na folha de estilo, e um `will-change: transform`
  // permanente mantem a pista numa camada de composicao propria a vida toda do
  // visor. No WebKit do telemovel essa camada e repintada do zero quando volta
  // a ser desenhada, e o quadro em que ela ainda esta vazia aparece BRANCO —
  // o clarao ao trocar de foto. Ligado so durante o gesto, a promocao dura o
  // percurso (que e o unico momento em que ela serve para alguma coisa) e a
  // camada e desfeita logo a seguir.
  willChange: isSwipeInProgress.value ? 'transform' : 'auto',
}));

const resetSwipe = () => {
  window.clearTimeout(swipeCommitTimer);
  swipeCommitTimer = null;
  swipeGesture = null;
  pendingSwipeDirection = 0;
  swipeTransitionMs.value = 0;
  swipeOffset.value = 0;
};

// A troca de indice do percurso ja confirmado, aplicada de uma vez. Existe
// como funcao propria porque tem DOIS gatilhos: o temporizador, no fim da
// animacao, e um novo toque que chegue antes dela terminar.
const finishSwipeCommit = () => {
  const direction = pendingSwipeDirection;
  if (!direction) return;

  resetSwipe();
  if (direction > 0) showPreviousAttachment();
  else showNextAttachment();
};

// Hook proprio, e nao uma linha no `onBeforeUnmount` la de cima: aquele fica
// centenas de linhas ANTES desta funcao existir, e chama-lo dali seria uso
// antes da definicao. O que ele evita e concreto — fechar a galeria a meio da
// animacao deixava o temporizador do `commitSwipe` a disparar num componente
// que ja nao existe.
onBeforeUnmount(resetSwipe);

// Termina o percurso ate a borda e so DEPOIS troca o indice. Trocar a foto no
// instante do largar devolveria o salto que este trabalho remove: a imagem
// nova apareceria ja centrada enquanto a antiga ainda estava a sair.
const commitSwipe = direction => {
  const width = swipeStageWidth();
  pendingSwipeDirection = direction;
  swipeTransitionMs.value = SWIPE_ANIMATION_MS;
  swipeOffset.value = direction * width;

  swipeCommitTimer = window.setTimeout(finishSwipeCommit, SWIPE_ANIMATION_MS);
};

const releaseSwipe = () => {
  swipeTransitionMs.value = SWIPE_ANIMATION_MS;
  swipeOffset.value = 0;
};

const onGalleryTouchStart = event => {
  if (!hasMoreThanOneAttachment.value || event.touches.length !== 1) return;

  galleryTouchStart.value = {
    x: event.touches[0].clientX,
    y: event.touches[0].clientY,
  };

  if (!isMobileViewport.value || zoomScale.value > 1) return;

  // Um toque durante a animacao apanha a pista onde ela esta, em vez de
  // esperar que ela termine.
  //
  // Aqui estava um defeito meu: o toque limpava o temporizador e mais nada. Se
  // o dedo pousasse no meio do percurso e nao arrastasse (um toque simples,
  // que e o gesto para mostrar e esconder a barra), a troca de foto ficava por
  // fazer e a pista PARAVA deslocada uma tela inteira — a foto saia do sitio e
  // tudo o que depende de `isSwipeInProgress` (a legenda, as reaccoes, a
  // sombra) ficava escondido para sempre, ate a galeria ser fechada. Concluir
  // o percurso antes de comecar o gesto novo devolve a pista ao centro com a
  // foto certa.
  finishSwipeCommit();
  swipeTransitionMs.value = 0;
  swipeGesture = {
    startX: event.touches[0].clientX,
    startY: event.touches[0].clientY,
    lastX: event.touches[0].clientX,
    lastAt: event.timeStamp,
    velocity: 0,
    // Ate saber se o dedo vai na horizontal ou na vertical, a pista nao se
    // mexe: caso contrario um gesto de fechar (vertical) arrastava a foto de
    // lado no primeiro pixel.
    axis: null,
  };
};

const onGalleryTouchMove = event => {
  if (!swipeGesture || event.touches.length !== 1) return;

  const touch = event.touches[0];
  const deltaX = touch.clientX - swipeGesture.startX;
  const deltaY = touch.clientY - swipeGesture.startY;

  if (!swipeGesture.axis) {
    if (Math.hypot(deltaX, deltaY) < 8) return;
    swipeGesture.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
  }

  if (swipeGesture.axis !== 'x') return;

  const elapsed = event.timeStamp - swipeGesture.lastAt;
  if (elapsed > 0) {
    swipeGesture.velocity = (touch.clientX - swipeGesture.lastX) / elapsed;
    swipeGesture.lastX = touch.clientX;
    swipeGesture.lastAt = event.timeStamp;
  }

  const isFirst = activeImageIndex.value === 0;
  const isLast = activeImageIndex.value === galleryAttachments.value.length - 1;
  const atEdge = (deltaX > 0 && isFirst) || (deltaX < 0 && isLast);

  swipeOffset.value = atEdge ? deltaX * SWIPE_EDGE_RESISTANCE : deltaX;
};

const onGalleryTouchEnd = event => {
  const gesture = swipeGesture;
  swipeGesture = null;

  if (gesture?.axis === 'x') {
    const width = swipeStageWidth();
    const offset = swipeOffset.value;
    const direction = offset > 0 ? 1 : -1;
    const isFirst = activeImageIndex.value === 0;
    const isLast =
      activeImageIndex.value === galleryAttachments.value.length - 1;
    const blocked = (direction > 0 && isFirst) || (direction < 0 && isLast);

    // Duas maneiras de confirmar, como em qualquer folheador de fotos: passar
    // de ~1/5 do ecra, ou soltar com velocidade mesmo tendo andado pouco. So a
    // distancia obrigaria a arrastar meia tela para um gesto rapido valer.
    const passedDistance = Math.abs(offset) > width * SWIPE_COMMIT_RATIO;
    const flicked = Math.abs(gesture.velocity) > SWIPE_COMMIT_VELOCITY;

    if (!blocked && (passedDistance || flicked)) commitSwipe(direction);
    else releaseSwipe();

    galleryTouchStart.value = null;
    return;
  }

  if (!galleryTouchStart.value || event.changedTouches.length !== 1) return;

  const touch = event.changedTouches[0];
  const deltaX = touch.clientX - galleryTouchStart.value.x;
  const deltaY = touch.clientY - galleryTouchStart.value.y;
  galleryTouchStart.value = null;

  if (zoomScale.value > 1 || Math.abs(deltaX) < 48) return;
  if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

  if (deltaX > 0) showPreviousAttachment();
  else showNextAttachment();
};

const onShowInChat = () => {
  const messageId = activeMessageId.value;
  if (!messageId) return;

  onClose();
  window.setTimeout(() => {
    document.getElementById(`message${messageId}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, 0);
};

const onShowAllMedia = () => {
  const conversationId = selectedChat.value?.id;
  if (!conversationId) return;

  setContactMediaReturn({ conversationId, tab: 'media' });
  showMobileMenu.value = false;
  onClose();
  updateUISettings({ is_contact_sidebar_open: true });
};

const mobileMenuItems = computed(() => [
  {
    label: galleryLabels.allMedia,
    value: 'all-media',
    action: 'all-media',
    icon: 'i-lucide-images',
    disabled: !selectedChat.value?.id,
  },
  {
    label: galleryLabels.goToMessage,
    value: 'go-to-message',
    action: 'go-to-message',
    icon: 'i-lucide-message-square-text',
    disabled: !activeMessageId.value,
  },
]);

const toggleMobileMenu = () => {
  showReactionPicker.value = false;
  showFullEmojiPicker.value = false;
  showMobileMenu.value = !showMobileMenu.value;
};

const closeMobileMenu = () => {
  showMobileMenu.value = false;
};

const onMobileMenuAction = ({ action }) => {
  if (action === 'all-media') {
    onShowAllMedia();
    return;
  }

  if (action === 'go-to-message') onShowInChat();
};

const onReplyToMessage = () => {
  const messageId = activeMessageId.value;
  const conversationId = selectedChat.value?.id;
  if (!messageId || !conversationId) return;

  LocalStorage.updateJsonStore(
    LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
    conversationId,
    messageId
  );
  // Aceleres: no telemovel a foto continua aberta e o compositor (com a
  // pre-visualizacao da resposta e o teclado) sobe por cima dela, como no
  // WhatsApp. O ReplyBox ja foca o editor ao receber o evento abaixo.
  if (isMobileViewport.value) {
    isReplyingHere.value = true;
    galleryReplyOpen.value = true;
  }
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, activeMessagePayload.value);
  if (!isMobileViewport.value) onClose();
};

// O X da pre-visualizacao emite o evento sem mensagem: volta ao visor normal.
// Enviar a resposta fecha o visor e leva a conversa, como no WhatsApp.
const onReplyToggledOutside = message => {
  if (!message) stopReplyingHere();
};
const onReplySentFromGallery = () => {
  if (isReplyingHere.value) onClose();
};
emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, onReplyToggledOutside);
emitter.on(BUS_EVENTS.MESSAGE_SENT, onReplySentFromGallery);
onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, onReplyToggledOutside);
  emitter.off(BUS_EVENTS.MESSAGE_SENT, onReplySentFromGallery);
  stopReplyingHere();
});

const onToggleStar = async () => {
  if (!activeMessageId.value || pendingMessageAction.value) return;

  const wasStarred = isStarred.value;
  try {
    pendingMessageAction.value = 'star';
    await store.dispatch('starMessage', {
      accountId: currentAccountId.value,
      messageId: activeMessageId.value,
    });
    useAlert(
      wasStarred
        ? t('CONVERSATION.CONTEXT_MENU.UNSTARRED')
        : t('CONVERSATION.CONTEXT_MENU.STARRED')
    );
  } catch (error) {
    useAlert('Não foi possível alterar o destaque da mensagem.');
  } finally {
    pendingMessageAction.value = '';
  }
};

const pinDurationDialogRef = ref(null);

const onTogglePin = async () => {
  if (!activeMessageId.value || pendingMessageAction.value) return;

  const wasPinned = isPinned.value;
  // Fixar passa antes pelo modal de período, como no WhatsApp; desafixar não,
  // porque o endpoint alterna e não há prazo a escolher.
  let duration = null;
  if (!wasPinned) {
    duration = await pinDurationDialogRef.value?.prompt();
    if (!duration) return;
  }
  try {
    pendingMessageAction.value = 'pin';
    await store.dispatch('pinMessage', {
      accountId: currentAccountId.value,
      conversationId: selectedChat.value?.id,
      messageId: activeMessageId.value,
      duration,
    });
    useAlert(
      wasPinned
        ? t('CONVERSATION.CONTEXT_MENU.UNPINNED')
        : t('CONVERSATION.CONTEXT_MENU.PINNED')
    );
  } catch (error) {
    useAlert('Não foi possível alterar a mensagem fixada.');
  } finally {
    pendingMessageAction.value = '';
  }
};

const toggleReactionPicker = () => {
  showFullEmojiPicker.value = false;
  showReactionPicker.value = !showReactionPicker.value;
};

const onReactToMessage = emoji => {
  if (!activeMessageId.value) return;

  store.dispatch('reactToMessage', {
    accountId: currentAccountId.value,
    messageId: activeMessageId.value,
    emoji,
  });
  closeToolbarPopovers();
};

const onOpenForwardModal = () => {
  if (!activeMessageId.value) return;
  closeToolbarPopovers();
  showForwardModal.value = true;
};

const onClickDownload = async () => {
  const { extension } = activeAttachment.value;
  const type = attachmentFileType(activeAttachment.value);
  if (!Object.values(ALLOWED_FILE_TYPES).includes(type)) return;

  try {
    isDownloading.value = true;
    const url = isGuardedMediaUrl(activeMediaSource.value)
      ? await resolveGuardedMedia(activeMediaSource.value)
      : activeMediaSource.value;
    if (!url) throw new Error('mídia indisponível');

    await downloadFile({ url, type, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const keyboardEvents = {
  Escape: {
    action: () => {
      if (showForwardModal.value) {
        showForwardModal.value = false;
      } else if (showMobileMenu.value) {
        showMobileMenu.value = false;
      } else if (showReactionPicker.value) {
        closeToolbarPopovers();
      } else if (
        canShowMobileMediaOverview.value &&
        !showMobileMediaOverview.value
      ) {
        onMobileDetailBack();
      } else {
        onClose();
      }
    },
  },
  ArrowLeft: {
    action: () => {
      if (hasMoreThanOneAttachment.value && !isImageZoomed.value)
        showPreviousAttachment();
    },
  },
  ArrowRight: {
    action: () => {
      if (hasMoreThanOneAttachment.value && !isImageZoomed.value)
        showNextAttachment();
    },
  },
};

useKeyboardEvents(keyboardEvents);

onMounted(() => {
  setImageAndVideoSrc(props.attachment);
  centerActiveThumbnail('auto');
  document.addEventListener('fullscreenchange', onFullscreenChange);
});
</script>

<template>
  <TeleportWithDirection :to="teleportTarget">
    <section
      v-if="show"
      ref="galleryOverlayRef"
      class="gallery-viewer-overlay aceleres-whatsapp-media-viewer absolute inset-0 flex size-full select-none flex-col overflow-hidden bg-white md:bg-n-background"
      :class="{ 'gallery-video-mobile-overlay': isVideo && isMobileViewport, 'gallery-contact-avatar-mobile': isMobileContactAvatar }"
      role="dialog"
      aria-modal="true"
      @click="onOverlayClick"
    >
      <template v-if="showMobileMediaOverview && isMobileViewport">
        <header
          class="relative z-30 flex min-h-[calc(4.25rem+var(--gallery-safe-top))] w-full flex-shrink-0 items-center bg-white px-2 pt-[var(--gallery-safe-top)] text-black md:hidden"
          @click.stop
        >
          <button
            type="button"
            class="relative z-10 inline-flex size-12 flex-shrink-0 items-center justify-center rounded-full text-black hover:bg-black/5"
            :aria-label="galleryLabels.back"
            :title="galleryLabels.back"
            @click="onClose"
          >
            <span class="i-lucide-arrow-left size-7" aria-hidden="true" />
          </button>

          <div
            class="pointer-events-none absolute left-1/2 flex max-w-[calc(100%_-_7rem)] -translate-x-1/2 flex-col items-center text-center"
          >
            <span class="w-full truncate text-sm font-semibold leading-4">
              {{ mobileOverviewTitle }}
            </span>
            <span class="w-full truncate text-xs leading-4 text-black/60">
              {{ mobileMediaCountLabel }}
            </span>
          </div>
        </header>

        <main
          class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain bg-black pb-[var(--gallery-safe-bottom)] md:hidden"
          @click.stop
        >
          <button
            v-for="thumbnailItem in mobileOverviewItems"
            :key="attachmentKey(thumbnailItem.attachment)"
            type="button"
            class="relative flex w-full flex-shrink-0 flex-col items-center justify-center bg-black"
            :aria-label="mobileOverviewAttachmentLabel(thumbnailItem.index)"
            @click="openMobileOverviewAttachment(thumbnailItem.attachment)"
          >
            <span
              class="relative flex h-[min(42dvh,20rem)] min-h-48 w-full items-center justify-center overflow-hidden bg-black"
            >
              <span
                v-if="isThumbnailUnavailable(thumbnailItem.attachment)"
                class="flex size-full items-center justify-center bg-white/5"
                aria-hidden="true"
              >
                <span class="i-lucide-image-off size-10 text-white/50" />
              </span>
              <span
                v-else-if="isThumbnailPending(thumbnailItem.attachment)"
                class="flex size-full animate-pulse items-center justify-center bg-white/5"
                aria-hidden="true"
              >
                <span class="i-lucide-image size-10 text-white/30" />
              </span>
              <LottieStickerCanvas
                v-else-if="isGalleryLottieSticker(thumbnailItem.attachment)"
                :url="thumbnailSource(thumbnailItem.attachment)"
                class="size-full rounded-sm"
              />
              <img
                v-else-if="
                  attachmentFileType(thumbnailItem.attachment) ===
                  ALLOWED_FILE_TYPES.IMAGE
                "
                :src="thumbnailSource(thumbnailItem.attachment)"
                :width="thumbnailItem.attachment.width || undefined"
                :height="thumbnailItem.attachment.height || undefined"
                class="size-full object-cover"
                loading="lazy"
                decoding="async"
                aria-hidden="true"
                @error="onThumbnailError($event, thumbnailItem.attachment)"
              />
              <video
                v-else-if="
                  [
                    ALLOWED_FILE_TYPES.VIDEO,
                    ALLOWED_FILE_TYPES.IG_REEL,
                  ].includes(attachmentFileType(thumbnailItem.attachment))
                "
                :src="thumbnailSource(thumbnailItem.attachment)"
                class="max-h-[65dvh] w-full object-contain"
                muted
                playsinline
                preload="metadata"
                aria-hidden="true"
                @error="onThumbnailError($event, thumbnailItem.attachment)"
              />
              <span
                v-else
                class="flex min-h-48 w-full items-center justify-center bg-white/5"
                aria-hidden="true"
              >
                <span class="i-lucide-file size-10 text-white/60" />
              </span>

              <time
                v-if="mobileOverviewMediaTime(thumbnailItem.attachment)"
                class="pointer-events-none absolute bottom-3 right-4 z-20 whitespace-nowrap rounded-md bg-black/75 px-2 py-1 text-xs font-medium tabular-nums text-white shadow-sm ring-1 ring-white/20"
              >
                {{ mobileOverviewMediaTime(thumbnailItem.attachment) }}
              </time>
            </span>
            <span class="h-2 w-full bg-white/5" aria-hidden="true" />
          </button>
        </main>
      </template>

      <header
        v-if="
          !showMobileMediaOverview &&
          !isContactAvatar &&
          showsExtendedMediaActions &&
          isMobileChromeVisible
        "
        class="gallery-viewer-header relative z-30 flex w-full flex-shrink-0 items-center justify-between bg-white px-2 md:!hidden"
        @click.stop
      >
        <NextButton
          icon="i-lucide-arrow-left"
          slate
          ghost
          class="!rounded-full !bg-white/70 !text-black backdrop-blur-md hover:!bg-white/85 [&>span]:!size-7"
          :aria-label="galleryLabels.back"
          :title="galleryLabels.back"
          @click="onMobileDetailBack"
        />

        <div
          class="absolute left-1/2 flex max-w-[calc(100%_-_7rem)] -translate-x-1/2 flex-col items-center rounded-xl bg-white/70 px-3 py-1.5 text-center text-black backdrop-blur-md"
        >
          <span class="w-full truncate text-sm font-semibold leading-4">
            {{ senderDetails.name }}
          </span>
          <span class="w-full truncate text-[11px] leading-4 text-black/70">
            {{ readableTime }}
          </span>
        </div>

        <div v-on-click-outside="closeMobileMenu" class="relative">
          <NextButton
            icon="i-lucide-ellipsis"
            slate
            ghost
            class="!rounded-full !bg-white/70 !text-black backdrop-blur-md hover:!bg-white/85 [&>span]:!size-7"
            :class="{ '!bg-white/90': showMobileMenu }"
            :aria-label="galleryLabels.moreOptions"
            :title="galleryLabels.moreOptions"
            @click="toggleMobileMenu"
          />
          <DropdownMenu
            v-if="showMobileMenu"
            :menu-items="mobileMenuItems"
            class="right-0 top-full mt-1 min-w-48 [&_button]:!text-black [&_span]:!text-black"
            @action="onMobileMenuAction"
          />
        </div>
      </header>

      <div
        v-if="
          !showMobileMediaOverview &&
          isVideo &&
          isMobileViewport &&
          isMobileChromeVisible
        "
        class="gallery-viewer-mobile-video-controls z-20 flex flex-shrink-0 items-center gap-2 border-b border-n-weak bg-white px-3 py-2 text-black md:hidden"
        @click.stop
      >
        <span class="min-w-9 text-xs tabular-nums">{{
          videoCurrentTimeLabel
        }}</span>
        <input
          type="range"
          class="h-1 min-w-0 flex-1 accent-[#00a884]"
          min="0"
          :max="videoDuration || 0"
          step="0.1"
          :value="videoCurrentTime"
          :aria-label="galleryLabels.videoProgress"
          @pointerdown="isSeekingVideo = true"
          @input="onSeekInput"
          @change="onSeekCommit"
          @pointerup="onSeekCommit"
        />
        <span class="min-w-9 text-right text-xs tabular-nums">{{
          mobileVideoRemainingTimeLabel
        }}</span>
        <button
          type="button"
          class="h-8 min-w-10 rounded-full bg-n-alpha-2 px-2 text-xs font-semibold text-black"
          :aria-label="galleryLabels.playbackSpeed"
          :title="galleryLabels.playbackSpeed"
          @click="cyclePlaybackRate"
        >
          {{ mobileVideoSpeedLabel }}
        </button>
        <button
          type="button"
          class="inline-flex size-8 flex-shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-black"
          :aria-label="
            isVideoFullscreen
              ? galleryLabels.exitFullscreen
              : galleryLabels.enterFullscreen
          "
          :title="
            isVideoFullscreen
              ? galleryLabels.exitFullscreen
              : galleryLabels.enterFullscreen
          "
          @click="toggleVideoFullscreen"
        >
          <span
            :class="
              isVideoFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'
            "
            class="size-5"
          />
        </button>
      </div>

      <header
        v-if="!showMobileMediaOverview"
        class="gallery-viewer-header z-20 flex h-[4.25rem] w-full flex-shrink-0 items-center gap-4 bg-n-background px-4 py-2 sm:px-6"
        :class="{
          'gallery-viewer-header--bare': isContactAvatar,
          '!justify-end': isMobileContactAvatar,
          '!hidden md:!flex': !isContactAvatar && showsExtendedMediaActions,
        }"
        @click.stop
      >
        <div
          v-if="!isContactAvatar"
          class="gallery-viewer-sender flex min-w-0 flex-1 items-center"
        >
          <Avatar
            :name="senderDetails.name"
            :src="senderDetails.avatar"
            :size="40"
            rounded-full
            class="flex-shrink-0"
          />
          <div
            class="ml-3 flex min-w-0 flex-col overflow-hidden rtl:ml-0 rtl:mr-3"
          >
            <h3 class="m-0 text-sm font-semibold leading-5 sm:text-base">
              <span
                class="overflow-hidden text-n-slate-12 whitespace-nowrap text-ellipsis"
              >
                {{ senderDetails.name }}
              </span>
            </h3>
            <span
              class="whitespace-nowrap text-xs text-n-slate-11 text-ellipsis"
            >
              {{ readableTime }}
            </span>
          </div>
        </div>

        <div
          v-if="!isContactAvatar"
          class="gallery-toolbar ml-2 flex flex-shrink-0 items-center gap-2.5"
        >
          <NextButton
            v-if="showsImageZoomActions"
            icon="i-lucide-zoom-out"
            slate
            ghost
            class="gallery-action-zoom-out !rounded-full [&>span]:!size-[1.88rem]"
            :disabled="!canDecreaseImageZoom"
            :aria-label="galleryLabels.decreaseZoom"
            :title="galleryLabels.decreaseZoom"
            @click="decreaseImageZoom"
          />
          <NextButton
            v-if="showsImageZoomActions"
            icon="i-lucide-zoom-in"
            slate
            ghost
            class="gallery-action-zoom-in !rounded-full [&>span]:!size-[1.88rem]"
            :disabled="!canIncreaseImageZoom"
            :aria-label="galleryLabels.increaseZoom"
            :title="galleryLabels.increaseZoom"
            @click="increaseImageZoom"
          />
          <NextButton
            v-if="showsExtendedMediaActions"
            icon="i-lucide-message-square-text"
            slate
            ghost
            class="gallery-action-show-chat !rounded-full [&>span]:!size-[1.88rem]"
            :disabled="!activeMessageId"
            :aria-label="galleryLabels.showInChatAction"
            :title="galleryLabels.showInChatAction"
            @click="onShowInChat"
          />
          <NextButton
            icon="i-lucide-reply"
            slate
            ghost
            class="gallery-action-reply max-md:!hidden !rounded-full [&>span]:!size-[1.88rem]"
            :disabled="!activeMessageId"
            :aria-label="galleryLabels.reply"
            :title="galleryLabels.reply"
            @click="onReplyToMessage"
          />
          <NextButton
            :icon="isStarred ? 'i-lucide-star-off' : 'i-lucide-star'"
            slate
            ghost
            class="gallery-action-star !rounded-full [&>span]:!size-[1.88rem]"
            :is-loading="pendingMessageAction === 'star'"
            :disabled="!activeMessageId || Boolean(pendingMessageAction)"
            :aria-label="
              isStarred ? galleryLabels.removeStar : galleryLabels.star
            "
            :title="isStarred ? galleryLabels.removeStar : galleryLabels.star"
            @click="onToggleStar"
          />
          <NextButton
            :icon="isPinned ? 'i-lucide-pin-off' : 'i-lucide-pin'"
            slate
            ghost
            class="gallery-action-pin !rounded-full [&>span]:!size-[1.88rem]"
            :is-loading="pendingMessageAction === 'pin'"
            :disabled="!activeMessageId || Boolean(pendingMessageAction)"
            :aria-label="isPinned ? galleryLabels.unpin : galleryLabels.pin"
            :title="isPinned ? galleryLabels.unpin : galleryLabels.pin"
            @click="onTogglePin"
          />
          <div
            v-if="showsExtendedMediaActions"
            class="gallery-action-reaction relative flex flex-shrink-0 max-md:!hidden"
          >
            <NextButton
              icon="i-lucide-smile-plus"
              slate
              ghost
              class="!rounded-full [&>span]:!size-[1.88rem]"
              :class="{ '!bg-n-alpha-2': showReactionPicker }"
              :disabled="!activeMessageId"
              :aria-label="galleryLabels.react"
              :title="galleryLabels.react"
              @click="toggleReactionPicker"
            />
            <div
              v-if="
                showReactionPicker && !(isMobileViewport && showFullEmojiPicker)
              "
              :key="showFullEmojiPicker ? 'emojis' : 'reacoes'"
              class="gallery-reaction-popover absolute right-0 top-[calc(100%+0.5rem)] z-30 overflow-hidden border border-n-weak bg-n-solid-1 shadow-lg max-md:origin-top-right"
              :class="
                showFullEmojiPicker
                  ? 'rounded-xl animate-[fade-in-up_0.16s_ease-out_both]'
                  : 'rounded-full px-1.5 animate-reaction-pop-in'
              "
              @click.stop
            >
              <MessageReactionBar
                v-if="!showFullEmojiPicker"
                :channel-type="reactionChannelType"
                @react="onReactToMessage"
                @more="showFullEmojiPicker = true"
                @close="closeToolbarPopovers"
              />
              <WhatsAppEmojiPicker
                v-else-if="!isMobileViewport"
                embedded
                :show-recent-reactions="false"
                :on-click="onReactToMessage"
              />
            </div>
          </div>
          <NextButton
            slate
            ghost
            class="gallery-action-forward !rounded-full [&>span]:!size-[1.88rem]"
            :class="{
              'gallery-action-forward-sticker-mobile !size-9 !bg-[#8c8c8c] !text-white hover:enabled:!bg-[#777777]':
                isMobileViewport && activeSticker,
            }"
            :disabled="!activeMessageId"
            :aria-label="galleryLabels.forward"
            :title="galleryLabels.forward"
            @click="onOpenForwardModal"
          >
            <template #icon>
              <svg
                v-if="isMobileViewport && activeSticker"
                class="size-5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M14 8V4l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" />
              </svg>
              <Icon v-else icon="i-tabler-corner-up-right-double" />
            </template>
          </NextButton>
          <NextButton
            v-if="showsExtendedMediaActions"
            icon="i-lucide-download"
            slate
            ghost
            class="gallery-action-download !rounded-full [&>span]:!size-[1.88rem]"
            :is-loading="isDownloading"
            :disabled="isDownloading"
            :aria-label="$t('CONVERSATION.DOWNLOAD')"
            :title="$t('CONVERSATION.DOWNLOAD')"
            @click="onClickDownload"
          />
        </div>
        <!-- Aceleres 2026-08-17: com a foto de perfil aberta, o topo diz de quem
             ela e — a foto redonda e o nome, a ESQUERDA, sem nenhum fundo
             atras. O cabecalho neste modo e flutuante e ocupa a largura toda
             (`gallery-viewer-header--bare`), com `space-between`: este bloco
             encosta a esquerda e o X fica na outra ponta. `senderDetails` e o
             mesmo do cabecalho normal: quem abre a foto monta o anexo com
             `sender`, em ContactInfo.openAvatarViewer. -->
        <div
          v-if="isContactAvatar && !isMobileContactAvatar"
          class="gallery-viewer-avatar-identity flex min-w-0 items-center gap-2"
        >
          <Avatar
            :name="senderDetails.name"
            :src="senderDetails.avatar"
            :size="32"
            rounded-full
            class="flex-shrink-0"
          />
          <div class="flex min-w-0 flex-col">
            <span class="truncate text-sm font-semibold text-n-slate-12">
              {{ isMobileContactAvatar ? activeAttachment.contactName || senderDetails.name : senderDetails.name }}
            </span>
            <span
              v-if="isMobileContactAvatar && activeAttachment.contactPhone"
              class="truncate text-xs"
            >
              {{ activeAttachment.contactPhone }}
            </span>
          </div>
        </div>
        <NextButton
          icon="i-lucide-x"
          slate
          ghost
          class="gallery-viewer-close flex-shrink-0 !rounded-full [&>span]:!size-[1.88rem]"
          :aria-label="$t('GENERAL.CLOSE')"
          :title="$t('GENERAL.CLOSE')"
          @click="onClose"
        />
      </header>

      <main
        v-if="!showMobileMediaOverview"
        ref="galleryStageRef"
        class="gallery-viewer-stage flex min-h-0 flex-1 items-stretch overflow-hidden bg-black md:bg-n-background"
      >
        <div
          v-if="!isContactAvatar && !isImageZoomed"
          class="gallery-viewer-nav gallery-viewer-nav--previous flex w-14 flex-shrink-0 items-center justify-center sm:w-36"
        >
          <NextButton
            v-if="hasMoreThanOneAttachment"
            icon="ltr:i-lucide-chevron-left rtl:i-lucide-chevron-right"
            :class="galleryNavigationButtonClass"
            size="lg"
            slate
            solid
            :disabled="activeImageIndex === 0"
            :aria-label="galleryLabels.previousImage"
            :title="galleryLabels.previousImage"
            @click.stop="showPreviousAttachment"
          />
        </div>

        <div
          class="gallery-viewer-content flex min-h-0 min-w-0 flex-1 touch-none items-center justify-center overflow-hidden p-1 md:touch-auto sm:p-4"
          @touchstart.passive="onGalleryTouchStart"
          @touchmove.passive="onGalleryTouchMove"
          @touchend.passive="onGalleryTouchEnd"
          @touchcancel.passive="onGalleryTouchEnd"
          @wheel.prevent.stop="onGalleryWheel"
        >
          <!-- Aceleres 2026-09-09: a PISTA. O palco acima continua parado e
               recorta (`overflow-hidden`); e esta caixa que se desloca com o
               dedo, levando a foto activa e as duas vizinhas de uma so vez.
               Sem ela o deslocamento teria de ser aplicado a cada tipo de
               media, e no computador ficaria em 0 na mesma — a pista nao muda
               nada la, so passa o espaco adiante. -->
          <div
            ref="galleryTrackRef"
            class="acl-gallery-track"
            :style="galleryTrackStyle"
          >
            <!-- As vizinhas: fora do ecra a uma largura de distancia, entram
                 pela borda a medida que a pista anda. Sao `aria-hidden` e sem
                 eventos — quem le a galeria por leitor de ecra continua a ver
                 uma foto de cada vez. -->
            <template v-for="(peekAttachment, direction) in {
              prev: previousPeekAttachment,
              next: nextPeekAttachment,
            }" :key="direction">
              <LottieStickerCanvas
                v-if="peekAttachment && isGalleryLottieSticker(peekAttachment) && thumbnailSource(peekAttachment)"
                :key="attachmentKey(peekAttachment)"
                :url="thumbnailSource(peekAttachment)"
                class="acl-gallery-peek"
                :class="`acl-gallery-peek--${direction}`"
                aria-hidden="true"
              />
              <img
                v-else-if="peekAttachment && thumbnailSource(peekAttachment)"
                class="acl-gallery-peek"
                :class="`acl-gallery-peek--${direction}`"
                :src="thumbnailSource(peekAttachment)"
                alt=""
                aria-hidden="true"
                draggable="false"
              />
            </template>
            <div
              v-if="isActiveMediaUnavailable"
              class="flex max-w-[280px] flex-col items-center justify-center gap-3 rounded-lg bg-n-alpha-2 px-6 py-5 text-center"
            >
              <span class="i-lucide-circle-off size-7 text-n-slate-11" />
              <p class="text-sm leading-5 text-n-slate-11">
                {{ galleryLabels.mediaUnavailable }}
              </p>
              <button
                type="button"
                class="text-sm font-medium text-n-brand underline"
                @click.stop="retryActiveMedia"
              >
                {{ galleryLabels.retryMedia }}
              </button>
            </div>
            <div
              v-else-if="activeSticker"
              class="flex max-h-full max-w-full flex-col items-center justify-center"
            >
              <div
                :style="imageWrapperStyle"
                class="flex items-center justify-center origin-center"
              >
                <LottieStickerCanvas
                  v-if="isLottieSticker"
                  :key="activeMediaRenderKey"
                  :url="activeMediaDisplaySource"
                  :style="stickerBoxStyle"
                  continuous
                />
                <img
                  v-else
                  ref="imageRef"
                  :key="activeMediaRenderKey"
                  :src="activeMediaDisplaySource"
                  data-source-index="0"
                  :style="[imageStyle, stickerBoxStyle]"
                  class="object-contain duration-100 ease-in-out transform select-none"
                  :class="{ '!duration-0': isDragging }"
                  draggable="false"
                  @click.stop
                  @dblclick.stop="onStickerDoubleClickZoom"
                  @pointerdown.stop="!isMobileViewport && onPointerDown($event)"
                  @pointermove.stop="!isMobileViewport && onPointerMove($event)"
                  @pointerup.stop="!isMobileViewport && onPointerUp($event)"
                  @pointercancel.stop="!isMobileViewport && onPointerUp($event)"
                  @lostpointercapture="!isMobileViewport && onPointerUp($event)"
                  @dragstart.prevent
                  @error="onActiveMediaError"
                />
              </div>

              <div class="mt-3 flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  class="acl-sticker-save rounded-2xl border border-[#b8b8b8] bg-transparent px-5 py-3 text-sm font-medium text-[#00a884] transition-colors hover:bg-[#e7e7e7] disabled:cursor-wait disabled:opacity-60"
                  :disabled="
                    resolvingStickerLibrary ||
                    Boolean(savingStickerScope) ||
                    stickerSavedFor('user')
                  "
                  @click.stop="saveStickerToLibrary('user')"
                >
                  {{
                    resolvingStickerLibrary
                      ? 'Verificando...'
                      : stickerSavedFor('user')
                        ? 'Adicionada para mim'
                        : 'Adicionar para mim'
                  }}
                </button>
                <button
                  type="button"
                  class="acl-sticker-save rounded-2xl border border-[#b8b8b8] bg-transparent px-5 py-3 text-sm font-medium text-[#00a884] transition-colors hover:bg-[#e7e7e7] disabled:cursor-wait disabled:opacity-60"
                  :disabled="
                    resolvingStickerLibrary ||
                    Boolean(savingStickerScope) ||
                    stickerSavedFor('instance')
                  "
                  @click.stop="saveStickerToLibrary('instance')"
                >
                  {{
                    resolvingStickerLibrary
                      ? 'Verificando...'
                      : stickerSavedFor('instance')
                        ? 'Adicionada à instância'
                        : 'Adicionar à instância'
                  }}
                </button>
              </div>
            </div>

            <div
              v-else-if="isImage"
              :style="imageWrapperStyle"
              class="relative flex items-center justify-center origin-center"
              :class="{
                // Adjust dimensions when rotated 90/270 degrees to maintain visibility
                // and prevent image from overflowing container in different aspect ratios
                'w-[calc(100dvh-8rem)] h-[calc(100dvw-7rem)]':
                  activeImageRotation % 180 !== 0,
                'size-full': activeImageRotation % 180 === 0,
              }"
            >
              <!-- Aceleres 2026-09-09: no telemovel esta camada deixa de ser
                   DESFOCADA — `md:blur-md` mantem o desfoque so no computador.
                   Ela e a miniatura que ocupa o palco enquanto o arquivo grande
                   chega, e o desfoque era o sinal de "ainda nao e a foto final";
                   na pratica, cada troca de foto lia-se como um borrao seguido
                   de um salto para o nitido. Sem ele a miniatura entra como
                   esta — num ecra de 390px e ampliada cerca de 1,2x, diferenca
                   que quase nao se nota — e a foto real substitui-a sem degrau.
                   Apagar a camada inteira nao servia: o palco e preto, e a foto
                   passaria a nascer de um rectangulo vazio. -->
              <img
                v-if="activeImagePreviewSource"
                ref="imagePreviewRef"
                :key="`preview-${attachmentKey(activeAttachment)}`"
                :src="activeImagePreviewSource"
                :width="activeAttachment.width || undefined"
                :height="activeAttachment.height || undefined"
                :style="imageStyle"
                class="pointer-events-none absolute inset-0 m-auto max-h-full max-w-full select-none object-contain transition-opacity duration-200 md:blur-md"
                :class="[
                  isActiveImageLoaded ? 'opacity-0' : 'opacity-80',
                  { 'gallery-photo-mobile-full-image': isMobilePhoto },
                ]"
                aria-hidden="true"
                draggable="false"
                fetchpriority="high"
                @load="onActiveImagePreviewLoad"
              />
              <img
                ref="imageRef"
                :key="activeMediaRenderKey"
                :src="activeMediaDisplaySource"
                :width="activeAttachment.width || undefined"
                :height="activeAttachment.height || undefined"
                data-source-index="0"
                :style="[imageStyle, avatarBoxStyle, avatarFallbackStyle]"
                class="max-h-full max-w-full select-none object-contain transition-opacity duration-200 ease-in-out transform"
                :class="{
                  'gallery-viewer-avatar-image': isContactAvatar,
                  'gallery-photo-mobile-full-image': isMobilePhoto,
                  '!duration-0': isDragging,
                  'opacity-0': activeImagePreviewSource && !isActiveImageLoaded,
                  'opacity-100':
                    !activeImagePreviewSource || isActiveImageLoaded,
                }"
                draggable="false"
                @click.stop="onMediaSurfaceClick"
                @touchstart="onMobileMediaTouchStart"
                @touchmove="onMobileMediaTouchMove"
                @touchend="onMobileMediaTouchEnd"
                @touchcancel="onMobileMediaTouchEnd"
                @dblclick.stop="onDoubleClickZoomImage"
                @pointerdown.stop="onPointerDown"
                @pointermove.stop="onPointerMove"
                @pointerup.stop="onPointerUp"
                @pointercancel.stop="onPointerUp"
                @lostpointercapture="onPointerUp"
                @dragstart.prevent
                @load="onActiveImageLoad"
                @error="onActiveMediaError"
              />
            </div>

            <!--
              Aceleres 24/08/2026: o `v-if` e separado do encadeamento acima, entao
              um GIF que seja IMAGEM de verdade (`image/gif`, como o que o
              Instagram entrega do seletor do Giphy) casava nos DOIS blocos: o
              `<img>` desenhava o GIF certo e este `<video>` desenhava por baixo
              uma caixa vazia e encolhida, deslocada no palco. Um `<video>` nao
              reproduz `image/gif`.

              No WhatsApp o problema nunca apareceu porque la o GIF chega como MP4
              mudo (`file_type` video), e `isImage` e falso.

              O `<video>` continua a servir essa forma; a imagem fica com o `<img>`.
            -->
            <div
              v-else-if="isGifMedia && !isImage"
              class="flex size-full items-center justify-center"
            >
              <video
                ref="gifRef"
                :key="activeMediaRenderKey"
                :src="activeMediaDisplaySource"
                data-source-index="0"
                :style="[gifChatSizeStyle, mobileMediaTransformStyle]"
                class="max-h-full max-w-full select-none object-contain"
                autoplay
                muted
                loop
                playsinline
                disablePictureInPicture
                disableRemotePlayback
                @click.stop="onMediaSurfaceClick"
                @touchstart="onMobileMediaTouchStart"
                @touchmove="onMobileMediaTouchMove"
                @touchend="onMobileMediaTouchEnd"
                @touchcancel="onMobileMediaTouchEnd"
                @loadedmetadata="updateMediaReactionPosition"
                @error="onActiveMediaError"
              />
            </div>

            <div
              v-else-if="isVideo"
              ref="videoWrapperRef"
              class="acl-video-player acl-video-mobile-frame relative max-h-full max-w-full"
              :style="[
                { aspectRatio: videoAspectRatio },
                mobileMediaTransformStyle,
              ]"
              @click.stop="onMediaSurfaceClick"
              @pointerenter="showControlsOnPointerEnter"
              @pointerleave="hideControlsOnPointerLeave"
              @touchstart="onVideoMediaTouchStart"
              @touchmove="onMobileMediaTouchMove"
              @touchend="onMobileMediaTouchEnd"
              @touchcancel="onMobileMediaTouchEnd"
            >
              <video
                ref="videoRef"
                :key="activeMediaRenderKey"
                :src="activeMediaDisplaySource"
                data-source-index="0"
                playsInline
                preload="auto"
                disablePictureInPicture
                disableRemotePlayback
                class="acl-video-mobile-media block h-full w-full object-contain"
                @loadedmetadata="onVideoLoadedMetadata"
                @timeupdate="onVideoTimeUpdate"
                @playing="onVideoPlaying"
                @pause="onVideoPause"
                @ended="onVideoEnded"
                @error="onActiveMediaError"
              />

              <div
                v-if="!isMobileViewport"
                class="acl-video-vignette absolute inset-0 z-0 transition-opacity duration-200"
                :class="showVideoControls ? 'opacity-100' : 'opacity-0'"
              />

              <button
                v-if="!isVideoPlaying && !isMobileViewport"
                type="button"
                class="acl-video-center-play absolute inset-0 m-auto flex size-16 items-center justify-center rounded-full bg-white/70 text-black backdrop-blur-md hover:bg-white/85 md:bg-black/45 md:text-white md:hover:bg-black/60"
                :aria-label="galleryLabels.play"
                :title="galleryLabels.play"
                @click.stop="togglePlayPauseVideo"
              >
                <span class="i-lucide-play size-9" />
              </button>

              <button
                v-if="!isMobileViewport"
                type="button"
                class="acl-video-icon-btn acl-video-icon-btn--flat absolute left-2 top-2 z-10 !bg-white/70 !text-black backdrop-blur-md md:!bg-transparent md:!text-white sm:left-3 sm:top-3"
                :class="
                  showVideoControls
                    ? 'opacity-100'
                    : 'pointer-events-none opacity-0'
                "
                :aria-label="
                  isVideoFullscreen
                    ? galleryLabels.exitFullscreen
                    : galleryLabels.enterFullscreen
                "
                :title="
                  isVideoFullscreen
                    ? galleryLabels.exitFullscreen
                    : galleryLabels.enterFullscreen
                "
                @click.stop="toggleVideoFullscreen"
              >
                <span
                  :class="
                    isVideoFullscreen
                      ? 'i-lucide-minimize'
                      : 'i-lucide-maximize'
                  "
                  class="size-5 sm:size-6"
                />
              </button>

              <div
                class="acl-video-controls absolute inset-x-0 bottom-0 z-10 flex items-center gap-2 text-black transition-opacity duration-200 [&_.acl-video-time]:!text-black md:text-white md:[&_.acl-video-time]:!text-white sm:gap-3"
                :class="[
                  showVideoControls
                    ? 'opacity-100'
                    : 'pointer-events-none opacity-0',
                  isVideoFullscreen
                    ? 'acl-video-controls--fullscreen bg-white/70 px-3 py-2 backdrop-blur-md md:bg-transparent md:backdrop-blur-none'
                    : 'bg-gradient-to-t from-white/85 via-white/50 to-transparent px-3 py-2 md:from-black/75 md:via-black/40 sm:px-4',
                ]"
                @click.stop
              >
                <button
                  v-if="!isVideoFullscreen"
                  type="button"
                  class="acl-video-icon-btn acl-video-play-toggle !text-black md:!text-white"
                  :aria-label="
                    isVideoPlaying ? galleryLabels.pause : galleryLabels.play
                  "
                  :title="
                    isVideoPlaying ? galleryLabels.pause : galleryLabels.play
                  "
                  @click="togglePlayPauseVideo"
                >
                  <span
                    :class="isVideoPlaying ? 'i-lucide-pause' : 'i-lucide-play'"
                    class="size-5 sm:size-6"
                  />
                </button>

                <span v-if="isVideoFullscreen" class="acl-video-time">{{
                  videoCurrentTimeLabel
                }}</span>

                <input
                  type="range"
                  class="acl-video-range acl-video-progress min-w-0 flex-1"
                  min="0"
                  :max="videoDuration || 0"
                  step="0.1"
                  :value="videoCurrentTime"
                  :style="{ '--acl-range-fill': `${videoProgressPercent}%` }"
                  :aria-label="galleryLabels.videoProgress"
                  @pointerdown="isSeekingVideo = true"
                  @input="onSeekInput"
                  @change="onSeekCommit"
                  @pointerup="onSeekCommit"
                />

                <span v-if="isVideoFullscreen" class="acl-video-time">{{
                  videoDurationLabel
                }}</span>

                <button
                  v-if="!isVideoFullscreen"
                  type="button"
                  class="acl-video-speed-btn !bg-white/70 !text-black backdrop-blur-md md:!bg-white/10 md:!text-white"
                  :aria-label="galleryLabels.playbackSpeed"
                  :title="galleryLabels.playbackSpeed"
                  @click="cyclePlaybackRate"
                >
                  {{ videoSpeedLabel }}
                </button>

                <div
                  v-if="!isVideoFullscreen"
                  class="acl-video-volume relative flex flex-shrink-0 items-center"
                >
                  <div
                    class="acl-video-volume-popup absolute bottom-full left-1/2 mb-2 -translate-x-1/2"
                  >
                    <input
                      type="range"
                      class="acl-video-range acl-video-volume-vertical"
                      min="0"
                      max="1"
                      step="0.05"
                      :value="isVideoMuted ? 0 : videoVolume"
                      :style="{
                        '--acl-range-fill': `${(isVideoMuted ? 0 : videoVolume) * 100}%`,
                      }"
                      :aria-label="galleryLabels.volume"
                      @input="onVolumeInput"
                    />
                  </div>
                  <button
                    type="button"
                    class="acl-video-icon-btn !text-black md:!text-white"
                    :aria-label="
                      isVideoMuted ? galleryLabels.unmute : galleryLabels.mute
                    "
                    :title="
                      isVideoMuted ? galleryLabels.unmute : galleryLabels.mute
                    "
                    @click="toggleMuteVideo"
                  >
                    <span :class="volumeIconClass" class="size-5 sm:size-6" />
                  </button>
                </div>
              </div>
            </div>

            <audio
              v-else-if="isAudio"
              :key="activeMediaRenderKey"
              controls
              class="w-full max-w-md"
              @click.stop
              @error="onActiveMediaError"
            >
              <source :src="activeAudioSource" />
            </audio>
          </div>
        </div>

        <div
          v-if="!isContactAvatar && !isImageZoomed"
          class="gallery-viewer-nav gallery-viewer-nav--next flex w-14 flex-shrink-0 items-center justify-center sm:w-36"
        >
          <NextButton
            v-if="hasMoreThanOneAttachment"
            icon="ltr:i-lucide-chevron-right rtl:i-lucide-chevron-left"
            :class="galleryNavigationButtonClass"
            size="lg"
            slate
            solid
            :disabled="activeImageIndex === galleryAttachments.length - 1"
            :aria-label="galleryLabels.nextImage"
            :title="galleryLabels.nextImage"
            @click.stop="showNextAttachment"
          />
        </div>

      </main>

      <div
        v-if="isVideo && isMobileViewport && !showMobileMediaOverview"
        class="gallery-video-mobile-spacer pointer-events-none min-h-0 flex-1"
        aria-hidden="true"
      />

      <div
        v-if="
          !showMobileMediaOverview &&
          showsExtendedMediaActions &&
          mediaFrameStyle &&
          isMobileChromeVisible &&
          !isSwipeInProgress
        "
        class="pointer-events-none absolute z-20 md:hidden"
        :style="mediaFrameStyle"
        @click.stop
      >
        <!-- Mantém o gradiente existente. Para fotos, este quadro acompanha
             os limites transformados da imagem; outras mídias preservam
             a referência atual do seu chrome. -->
        <div
          v-if="isMobileMediaZoomed || captionPlacement === 'overlay'"
          class="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-n-black/80 via-n-black/35 to-transparent"
        />

        <div
          v-if="captionPlacement === 'overlay' && !isMobilePhoto"
          class="absolute bottom-14 left-3 flex justify-center"
          :class="activeReactions.length ? 'right-16' : 'right-3'"
        >
          <div
            class="pointer-events-auto max-h-28 max-w-full overflow-y-auto px-3 py-2 text-center text-sm text-white"
          >
            <FormattedContent
              class="!text-white [&_*]:!text-white"
              :content="activeMediaCaption"
            />
          </div>
        </div>

        <div
          v-if="activeReactions.length"
          class="pointer-events-auto absolute bottom-14 right-2"
          :class="{ 'gallery-video-mobile-reactions': isVideo }"
        >
          <MessageReactions :reactions="activeReactions" />
        </div>
      </div>

      <!-- A legenda e as ações da foto compartilham a mesma coluna.
           No zoom, ela segue os limites transformados da imagem sem
           ampliar a fonte do texto. -->
      <div
        v-if="!showMobileMediaOverview && isMobilePhoto"
        v-show="
          !isImageZoomed ||
          (mediaFrameStyle && isMobileChromeVisible && !isSwipeInProgress)
        "
        class="gallery-mobile-photo-details z-20 flex min-w-0 flex-col"
        :class="
          isImageZoomed
            ? 'pointer-events-none absolute justify-end'
            : 'flex-shrink-0'
        "
        :style="mobilePhotoDetailsStyle"
        @click.stop
      >
        <div
          v-if="activeMediaCaption"
          class="gallery-viewer-caption flex-shrink-0 overflow-y-auto text-sm"
          :class="[
            isImageZoomed
              ? 'pointer-events-auto ml-3 max-h-28 max-w-full px-3 py-2 text-left text-white'
              : 'z-20 px-4 py-2 text-center text-n-slate-12 max-md:max-h-[24dvh] max-md:bg-white max-md:px-4 max-md:py-3 max-md:text-left max-md:!text-[#000] max-md:leading-5',
            isImageZoomed ? (activeReactions.length ? 'mr-16' : 'mr-3') : '',
          ]"
        >
          <FormattedContent
            :class="
              isImageZoomed
                ? '!text-white [&_*]:!text-white'
                : '!text-[#000] [&_*]:!text-[#000]'
            "
            :content="activeMediaCaption"
          />
        </div>
        <div ref="mobilePhotoActionsRef" class="flex-shrink-0" />
      </div>

      <div
        v-if="!showMobileMediaOverview && activeReactions.length"
        class="gallery-viewer-reactions"
        :class="[
          mediaReactionStyle
            ? 'pointer-events-auto absolute z-20 -translate-y-full'
            : 'z-20 flex flex-shrink-0 justify-end px-4 pt-1',
          { '!hidden md:!block': showsExtendedMediaActions },
        ]"
        :style="mediaReactionStyle || undefined"
        @click.stop
      >
        <MessageReactions :reactions="activeReactions" />
      </div>

      <div
        v-if="
          !showMobileMediaOverview &&
          !isMobilePhoto &&
          captionPlacement !== 'hidden' &&
          !(isVideo && isMobileViewport)
        "
        class="gallery-viewer-caption z-20 flex-shrink-0 overflow-y-auto px-4 py-2 text-center text-sm text-n-slate-12"
        :class="{
          '!hidden md:!block': captionPlacement === 'overlay',
          'gallery-video-mobile-caption mx-auto w-fit max-w-[min(85vw,42rem)] max-h-[24dvh]':
            isVideo,
          'max-md:max-h-[24dvh] max-md:bg-white max-md:px-4 max-md:py-3 max-md:text-left max-md:!text-[#000] max-md:leading-5':
            captionPlacement === 'mobile-flow',
          'acl-instagram-question-caption max-md:bg-white max-md:px-4 max-md:py-3 max-md:text-left max-md:leading-5':
            isInstagramQuestionVideo,
        }"
        @click.stop
      >
        <FormattedContent
          :class="[
            captionPlacement === 'mobile-flow'
              ? '!text-[#000] [&_*]:!text-[#000]'
              : '',
            isVideo ? 'block !max-w-none break-words' : '',
          ]"
          :content="activeMediaCaption"
        />
      </div>

      <div
        v-if="!showMobileMediaOverview && isVideo && isMobileViewport && activeMediaCaption"
        class="gallery-video-mobile-caption-text z-20 flex-shrink-0 px-4 py-2 text-sm text-white"
        @click.stop
      >
        <div v-if="!mobileVideoCaptionExpanded" class="flex min-w-0 items-center gap-2">
          <span ref="mobileVideoCaptionTextRef" class="min-w-0 flex-1 truncate">
            {{ activeMediaCaption }}
          </span>
          <button
            v-if="mobileVideoCaptionOverflowing"
            type="button"
            class="flex-shrink-0 font-semibold text-white underline"
            :aria-expanded="false"
            @click="mobileVideoCaptionExpanded = true"
          >
            Ler mais
          </button>
        </div>
        <div v-else class="max-h-[24dvh] overflow-y-auto break-words">
          <FormattedContent class="!text-white [&_*]:!text-white" :content="activeMediaCaption" />
          <button
            type="button"
            class="mt-1 font-semibold text-white underline"
            :aria-expanded="true"
            @click="mobileVideoCaptionExpanded = false"
          >
            Ler menos
          </button>
        </div>
      </div>

      <div
        v-if="!showMobileMediaOverview && isVideo && isMobileViewport"
        ref="mobileVideoActionsRef"
        class="gallery-video-mobile-action-row relative z-30 flex-shrink-0"
      />

      <div
        v-if="
          !showMobileMediaOverview &&
          isMobileViewport &&
          showsExtendedMediaActions &&
          !isMobilePhoto &&
          !isVideo
        "
        ref="mobileMediaActionsRef"
        class="gallery-mobile-media-action-row relative z-30 flex-shrink-0"
      />

      <Teleport v-if="mobileActionTarget" :to="mobileActionTarget">
        <div
          v-if="
            showsExtendedMediaActions &&
            !isActiveMediaUnavailable &&
            isMobileChromeVisible &&
            !isReplyingHere &&
            (!isVideo || mobileMediaActionStyle || mobileVideoActionsRef)
          "
          class="pointer-events-none z-30 flex items-center md:hidden"
          :class="[
            isMobilePhoto
              ? 'relative px-2 py-2'
              : isVideo && isMobileViewport && mobileVideoActionsRef
                ? 'relative gallery-video-mobile-actions'
                : isVideo
                  ? 'absolute gallery-video-mobile-actions'
                  : 'relative px-2 py-2',
          ]"
          :style="
            isMobilePhoto && isImageZoomed
              ? mobilePhotoActionStyle
              : isVideo && !(isMobileViewport && mobileVideoActionsRef)
                ? mobileMediaActionStyle
                : undefined
          "
          @click.stop
        >
          <div class="gallery-mobile-reply-bar pointer-events-auto flex w-full min-w-0 items-center">
            <button
              type="button"
              class="gallery-mobile-reply-bar__reply flex min-w-0 flex-1 items-center text-left text-sm text-black disabled:opacity-50"
              :disabled="!activeMessageId"
              :aria-label="galleryLabels.reply"
              @click="onReplyToMessage"
            >
              {{ galleryLabels.reply }}
            </button>

            <div
              class="gallery-mobile-reply-bar__reaction relative flex flex-shrink-0"
            >
              <button
                type="button"
                class="gallery-mobile-reply-bar__emoji flex size-9 items-center justify-center rounded-full text-black disabled:opacity-50"
                :disabled="!activeMessageId"
                :aria-label="galleryLabels.react"
                :title="galleryLabels.react"
                @click="toggleReactionPicker"
              >
                <span class="i-lucide-smile-plus size-5" />
              </button>

              <div
                v-if="showReactionPicker && !showFullEmojiPicker"
                class="pointer-events-auto animate-reaction-pop-in absolute bottom-11 right-0 z-40 max-h-[min(26rem,60dvh)] max-w-[calc(100vw-1rem)] overflow-auto rounded-full border border-n-weak bg-n-solid-1 px-1.5 shadow-lg max-md:origin-bottom-right"
                @click.stop
              >
                <MessageReactionBar
                  :channel-type="reactionChannelType"
                  @react="onReactToMessage"
                  @more="showFullEmojiPicker = true"
                  @close="closeToolbarPopovers"
                />
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <footer
        v-if="
          !showMobileMediaOverview &&
          !isMobileContactAvatar &&
          (hasMoreThanOneAttachment || showsExtendedMediaActions) &&
          (!isImageHistoryHidden || isMobileViewport) &&
          (!showsExtendedMediaActions || isMobileChromeVisible) &&
          !isReplyingHere
        "
        class="gallery-viewer-footer z-20 flex flex-shrink-0 flex-col gap-1.5 overflow-hidden border-t border-n-weak bg-white px-3 py-2 md:bg-n-background"
        :class="{
          'md:!hidden': !hasMoreThanOneAttachment,
          '!h-[4.5rem] !min-h-[4.5rem] !pb-2 md:!h-[7.5rem] md:!min-h-[7.5rem]':
            showsExtendedMediaActions && !(isVideo && isMobileViewport),
        }"
        @click.stop
      >
        <div
          class="gallery-thumbnail-carousel flex min-h-0 min-w-0 flex-1 items-center gap-1"
        >
          <div
            ref="thumbnailTrackRef"
            class="gallery-thumbnail-track flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overflow-y-hidden overscroll-x-contain scroll-smooth"
            @wheel="onThumbnailWheel"
          >
            <button
              v-for="thumbnailItem in thumbnailItems"
              :key="attachmentKey(thumbnailItem.attachment)"
              type="button"
              class="relative size-16 flex-shrink-0 overflow-hidden rounded-md border-[3px] bg-n-alpha-2 p-0.5 transition-all sm:size-20"
              :class="[
                isMobileViewport &&
                [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
                  attachmentFileType(thumbnailItem.attachment)
                )
                  ? 'gallery-mobile-video-thumbnail'
                  : '',
                thumbnailItem.index === activeImageIndex
                  ? 'border-[#00a884] ring-2 ring-[#00a884]/40 ring-offset-1 ring-offset-n-background'
                  : 'border-transparent opacity-80 hover:border-n-strong hover:opacity-100',
              ]"
              :data-gallery-thumbnail-active="
                thumbnailItem.index === activeImageIndex
              "
              :aria-current="
                thumbnailItem.index === activeImageIndex ? 'true' : undefined
              "
              :aria-label="
                thumbnailLabel(thumbnailItem.attachment, thumbnailItem.index)
              "
              @click="
                onClickChangeAttachment(
                  thumbnailItem.attachment,
                  thumbnailItem.index
                )
              "
            >
              <span
                v-if="isThumbnailUnavailable(thumbnailItem.attachment)"
                class="flex size-full items-center justify-center rounded-sm bg-n-alpha-2"
              >
                <span
                  :class="[
                    [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
                      attachmentFileType(thumbnailItem.attachment)
                    )
                      ? 'i-lucide-video-off'
                      : 'i-lucide-image-off',
                    isMobileViewport &&
                    [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
                      attachmentFileType(thumbnailItem.attachment)
                    )
                      ? 'text-white'
                      : 'text-black',
                  ]"
                  class="size-6 md:text-n-slate-11"
                />
              </span>
              <!-- A midia guardada so aparece depois de baixada pelo cliente
                   autenticado. Ate la a celula fica em espera, e nao no icone
                   de indisponivel — que era o que piscava em todo o historico
                   enquanto ele carregava. -->
              <span
                v-else-if="isThumbnailPending(thumbnailItem.attachment)"
                class="flex size-full animate-pulse items-center justify-center rounded-sm bg-n-alpha-2"
                :class="{
                  'gallery-mobile-video-thumbnail-pending':
                    isMobileViewport &&
                    [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
                      attachmentFileType(thumbnailItem.attachment)
                    ),
                }"
              >
                <span
                  v-if="
                    isMobileViewport &&
                    [ALLOWED_FILE_TYPES.VIDEO, ALLOWED_FILE_TYPES.IG_REEL].includes(
                      attachmentFileType(thumbnailItem.attachment)
                    )
                  "
                  class="i-lucide-video size-5 text-white"
                  aria-hidden="true"
                />
              </span>
              <LottieStickerCanvas
                v-else-if="isMobileViewport && isGalleryLottieSticker(thumbnailItem.attachment)"
                :url="thumbnailSource(thumbnailItem.attachment)"
                class="size-full rounded-sm"
              />
              <img
                v-else-if="
                  attachmentFileType(thumbnailItem.attachment) ===
                  ALLOWED_FILE_TYPES.IMAGE
                "
                :src="thumbnailSource(thumbnailItem.attachment)"
                data-source-index="0"
                alt=""
                loading="lazy"
                class="size-full rounded-sm object-cover"
                @error="onThumbnailError($event, thumbnailItem.attachment)"
              />
              <video
                v-else-if="
                  [
                    ALLOWED_FILE_TYPES.VIDEO,
                    ALLOWED_FILE_TYPES.IG_REEL,
                  ].includes(attachmentFileType(thumbnailItem.attachment))
                "
                :src="thumbnailSource(thumbnailItem.attachment)"
                data-source-index="0"
                muted
                preload="metadata"
                class="pointer-events-none size-full rounded-sm object-cover"
                @error="onThumbnailError($event, thumbnailItem.attachment)"
              />
              <span
                v-else
                class="flex size-full items-center justify-center rounded-sm bg-n-alpha-2"
              >
                <span
                  class="i-lucide-audio-lines size-6 text-black md:text-n-slate-11"
                />
              </span>
              <span
                v-if="
                  !isAudio &&
                  (isThumbnailStarred(thumbnailItem.attachment) ||
                    isThumbnailPinned(thumbnailItem.attachment))
                "
                class="pointer-events-none absolute bottom-1 right-1 flex items-center gap-0.5 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]"
              >
                <Icon
                  v-if="isThumbnailStarred(thumbnailItem.attachment)"
                  icon="i-lucide-star"
                  class="size-3.5 fill-current"
                  aria-hidden="true"
                />
                <Icon
                  v-if="isThumbnailPinned(thumbnailItem.attachment)"
                  icon="i-lucide-pin"
                  class="size-3.5 fill-current"
                  aria-hidden="true"
                />
              </span>
            </button>
          </div>
        </div>
      </footer>

      <footer
        v-if="
          !showMobileMediaOverview &&
          showsExtendedMediaActions &&
          isMobileChromeVisible &&
          !isReplyingHere
        "
        class="gallery-viewer-mobile-actions-footer z-20 flex flex-shrink-0 items-center justify-between border-t border-n-weak bg-n-background px-4 pt-2 pb-[calc(0.5rem+var(--gallery-safe-bottom))] md:hidden"
        @click.stop
      >
        <button
          type="button"
          class="gallery-forward-mobile-button inline-flex size-9 items-center justify-center rounded-full !bg-[#8c8c8c] text-white transition-colors hover:!bg-[#777777] disabled:opacity-50"
          :disabled="!activeMessageId"
          :aria-label="galleryLabels.forwardMobile"
          :title="galleryLabels.forwardMobile"
          @click="onOpenForwardModal"
        >
          <svg
            class="gallery-forward-mobile-icon size-5"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M14 8V4l7 7-7 7v-4.1c-5 0-8.5 1.6-11 5.1 1-5 4-10 11-11z" />
          </svg>
        </button>

        <button
          v-if="isVideo"
          type="button"
          class="inline-flex size-14 items-center justify-center rounded-full bg-white/70 text-black backdrop-blur-md transition-colors hover:bg-white/85 disabled:opacity-50"
          :aria-label="
            isVideoPlaying ? galleryLabels.pause : galleryLabels.play
          "
          :title="isVideoPlaying ? galleryLabels.pause : galleryLabels.play"
          @click="togglePlayPauseVideo"
        >
          <span
            :class="isVideoPlaying ? 'i-lucide-pause' : 'i-lucide-play'"
            class="size-8"
          />
        </button>

        <button
          type="button"
          class="inline-flex size-14 items-center justify-center rounded-full bg-white/70 text-black backdrop-blur-md transition-colors hover:bg-white/85 disabled:opacity-50"
          :disabled="!activeMessageId || Boolean(pendingMessageAction)"
          :aria-label="
            isStarred ? galleryLabels.removeStar : galleryLabels.star
          "
          :title="isStarred ? galleryLabels.removeStar : galleryLabels.star"
          @click="onToggleStar"
        >
          <span
            :class="[
              pendingMessageAction === 'star'
                ? 'i-lucide-loader-circle animate-spin'
                : isStarred
                  ? 'i-lucide-star-off'
                  : 'i-lucide-star',
            ]"
            class="size-8"
          />
        </button>
      </footer>

      <!-- Período de fixação (Aceleres): abre antes de fixar, nunca ao desafixar. -->
      <PinDurationDialog ref="pinDurationDialogRef" />

      <ForwardMessageModal
        v-if="!showMobileMediaOverview && showForwardModal && selectedChat.id"
        :show="showForwardModal"
        :conversation-id="selectedChat.id"
        :selected-messages="[activeMessagePayload]"
        @cancel="showForwardModal = false"
        @update:show="showForwardModal = $event"
        @success="showForwardModal = false"
      />

      <!-- Aceleres 2026-09-15 (Facundo): no telemovel o seletor grande de
           reacoes e a folha unica de emojis, por cima da galeria. -->
      <MobileEmojiSheet
        :open="isMobileViewport && showReactionPicker && showFullEmojiPicker"
        :on-select="onReactToMessage"
        @close="closeToolbarPopovers"
      />
    </section>
  </TeleportWithDirection>
</template>

<style scoped>
/* Aceleres 2026-09-09: a pista do arrasto entre fotos.

   Ela e uma caixa transparente: ocupa o palco todo e centra o que tem dentro,
   exactamente como o palco fazia com os filhos directos antes de existir. O
   que ela acrescenta e um `transform` proprio — necessario porque o invólucro
   da foto ja carrega um `transform: rotate(...)` EM LINHA (useImageZoom), e
   estilo em linha vence qualquer regra de folha: sem uma caixa a mais, nao ha
   onde escrever o deslocamento sem apagar a rotacao.

   `translate3d` e nao `left`: o percurso fica na GPU e nao volta a calcular o
   layout do palco a cada quadro do dedo. */
.acl-gallery-track {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
}

/* As vizinhas ficam a uma largura de distancia, com 8px de folga para as duas
   nao se tocarem no meio do percurso. So existem no telemovel — no computador
   `previousPeekSource`/`nextPeekSource` devolvem vazio e nem chegam a ser
   desenhadas —, e esta regra e a segunda tranca, para o caso de a janela ser
   reduzida com a galeria ja aberta. */
.acl-gallery-peek {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  margin: auto;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
}

.acl-gallery-peek--prev {
  right: calc(100% + 8px);
}

.acl-gallery-peek--next {
  left: calc(100% + 8px);
}

@media (min-width: 768px) {
  .acl-gallery-peek {
    display: none;
  }
}

/* Aceleres 2026-08-17: modo "foto de perfil do contato". O cabecalho perde a
   faixa e vira apenas o X no canto superior direito, POR CIMA da imagem — sem
   ele reservando 4,25rem de altura, o <main> ocupa o visualizador inteiro e a
   foto (max-h-full/max-w-full) cresce junto. As colunas de navegacao ja saem
   pelo v-if no template, entao a largura tambem e toda da imagem. */
/* Seletor DUPLO de proposito: o bloco `@media (max-width: 767px)` la embaixo
   redeclara `position`, `height` e `padding` de `.gallery-viewer-header` e vem
   depois deste ponto do arquivo — com uma classe so, o modo compacto perderia
   no mobile. O `--gallery-safe-top` continua respeitado para o X nao cair sob
   o entalhe do celular (a variavel e definida no overlay). */
.gallery-viewer-header.gallery-viewer-header--bare {
  position: absolute;
  top: 0;
  /* Aceleres 2026-08-17: largura inteira, e nao mais so o canto direito — a
     identidade (foto redonda + nome) fica a ESQUERDA e o X segue a direita.
     Com `width: auto` os dois andavam colados no canto. */
  inset-inline: 0;
  width: auto;
  height: auto;
  min-height: 0;
  padding: calc(0.75rem + var(--gallery-safe-top, 0px)) 0.75rem 0.75rem;
  border-bottom: 0;
  background: transparent;
  justify-content: space-between;
}

/* Aceleres 2026-08-18: a foto de perfil aberta se ADAPTA ao espaco em vez de
   ser recortada ou esticada.

   Ate 2026-08-17 esta regra era `object-fit: cover` num quadro fixo de
   `min(700px, 90vmin)`. O quadro fixo foi uma escolha deliberada de entao —
   toda foto abrindo do mesmo tamanho, como no WhatsApp —, mas ele cobrava dois
   precos que a tarefa de agora pede para eliminar: `cover` CORTA qualquer foto
   que nao seja quadrada, e o quadro fixo ESTICAVA para 700px as fotos de 80/96
   px que a WAME entrega (`stp=dst-jpg_s96x96`, e pedir o original devolve 403),
   que e exatamente a ampliacao artificial de miniatura que nao se quer.

   `contain` resolve o corte: mantém a proporção original e mostra a foto
   inteira. A MEDIDA do quadro saiu daqui porque depende da resolução do
   arquivo e do zoom aplicado ao painel. `avatarBoxStyle` entrega cerca de
   700px visíveis para os originais com qualidade suficiente; fotos de 80/96px
   permanecem próximas da resolução real e não são artificialmente esticadas. */
.gallery-viewer-avatar-image {
  object-fit: contain;
  border-radius: 4px;
}

.gallery-viewer-overlay {
  z-index: 2147483500;
}

@media (max-width: 767px) {
  .gallery-contact-avatar-mobile {
    background: rgb(255 255 255 / 90%);
  }

  .gallery-contact-avatar-mobile .gallery-viewer-stage {
    background: transparent;
  }

  .gallery-contact-avatar-mobile .gallery-viewer-close {
    color: #111827 !important;
  }
}

.gallery-toolbar,
.gallery-thumbnail-track {
  scrollbar-width: none;
}

.gallery-toolbar::-webkit-scrollbar,
.gallery-thumbnail-track::-webkit-scrollbar {
  display: none;
}

.gallery-viewer-footer {
  height: 7.5rem;
  min-height: 7.5rem;
}

.gallery-viewer-footer__status {
  min-height: 1.25rem;
}

.gallery-thumbnail-carousel {
  min-height: 0;
}

.gallery-thumbnail-track {
  padding-inline: 0.25rem calc(50% - 2.5rem);
}

.gallery-thumbnail-track > button {
  border-radius: 9px;
}

.gallery-thumbnail-track > button > img {
  border-radius: 9px;
}

.gallery-thumbnail-index {
  pointer-events: none;
}

:global(.acl-forward-overlay) {
  z-index: 2147483600 !important;
}

.acl-video-icon-btn {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 9999px;
  color: #fff;
  background: transparent;
  transition:
    background-color 0.15s ease,
    opacity 0.2s ease;
}

/* Aceleres: sombra no proprio icone (nao so no fundo do botao), pra ele
   continuar legivel em cima de qualquer trecho claro ou escuro do video. */
.acl-video-icon-btn > span,
.acl-video-center-play > span {
  filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7))
    drop-shadow(0 0 1px rgba(0, 0, 0, 0.6));
}

.acl-video-icon-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* Aceleres: botao de tela cheia fica sempre sem fundo, so o icone com
   sombra (acima) garante a legibilidade sobre o video. */
.acl-video-icon-btn--flat,
.acl-video-icon-btn--flat:hover {
  background: transparent;
}

/* Aceleres: sombra ao redor do video para os controles (tela cheia, topo
   e rodape) continuarem legiveis mesmo sobre trechos claros do video. */
.acl-video-vignette {
  position: absolute;
  pointer-events: none;
  background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.5) 0%,
      rgba(0, 0, 0, 0) 22%
    ),
    radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0) 65%,
      rgba(0, 0, 0, 0.3) 100%
    );
}

/* Aceleres: em tela cheia os controles ficam minimalistas, no estilo do
   visualizador de video do WhatsApp (so a barra de progresso fina e o
   tempo nas pontas, sem fundo). */
.acl-video-controls--fullscreen {
  gap: 0.5rem;
}

.acl-video-time {
  flex-shrink: 0;
  min-width: 2.25rem;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
}

.acl-video-controls--fullscreen .acl-video-progress {
  height: 3px;
}

.acl-video-progress {
  min-width: 0;
}

.gallery-video-mobile-actions {
  box-sizing: border-box;
  align-items: flex-end;
  padding: 0.5rem;
}

@media (max-width: 767px) {
  .gallery-mobile-reply-bar {
    min-height: 2.75rem;
    padding: 0.25rem 0.375rem 0.25rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 1.5rem;
    background: #fff;
  }

  .gallery-mobile-reply-bar__reply {
    min-height: 2.25rem;
  }
}

.acl-video-volume-popup {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 5.5rem;
  padding: 0.5rem 0;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.75);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.acl-video-volume:hover .acl-video-volume-popup,
.acl-video-volume:focus-within .acl-video-volume-popup {
  opacity: 1;
  pointer-events: auto;
}

.acl-video-volume-vertical {
  width: 4.5rem;
  transform: rotate(-90deg);
}

.acl-video-speed-btn {
  flex-shrink: 0;
  min-width: 2.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  transition: background-color 0.15s ease;
}

.acl-video-speed-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

.acl-video-range {
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    #00a884 var(--acl-range-fill, 0%),
    rgba(255, 255, 255, 0.3) var(--acl-range-fill, 0%)
  );
  cursor: pointer;
  outline: none;
}

.acl-video-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #00a884;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
}

.acl-video-range::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background: #00a884;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
}

.acl-video-range::-moz-range-track {
  height: 4px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.3);
}

@media (max-width: 767px) {
  .gallery-video-mobile-overlay {
    background: #000;
  }

  .gallery-video-mobile-overlay .gallery-viewer-stage {
    position: absolute;
    inset: 0;
    height: 100%;
    z-index: 0;
  }

  .gallery-video-mobile-overlay .gallery-viewer-header {
    background: #fff;
    border-color: transparent;
  }

  .gallery-video-mobile-overlay .gallery-viewer-mobile-video-controls {
    width: calc(100% - 1rem);
    margin: 0.5rem auto 0;
    border: 0;
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.88);
  }

  .gallery-video-mobile-overlay .gallery-viewer-footer,
  .gallery-video-mobile-overlay .gallery-viewer-mobile-actions-footer {
    background: #fff;
    border-color: transparent;
  }

  .gallery-video-mobile-action-row,
  .gallery-video-mobile-caption-text {
    background: rgba(0, 0, 0, 0.35);
  }

  .gallery-video-mobile-actions {
    align-items: center;
  }

  .gallery-video-mobile-overlay .gallery-viewer-footer {
    height: calc(3.25rem + var(--gallery-safe-bottom));
    min-height: calc(3.25rem + var(--gallery-safe-bottom));
    padding-block: 0.25rem var(--gallery-safe-bottom);
  }

  .gallery-video-mobile-overlay .gallery-thumbnail-track > button {
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    border-width: 0;
    border-radius: 0;
  }

  .gallery-video-mobile-overlay .gallery-thumbnail-track {
    gap: 0;
    padding-inline: 0 calc(50% - 1.25rem);
    scroll-behavior: auto;
  }

  .gallery-video-mobile-overlay .gallery-thumbnail-track > button > img,
  .gallery-video-mobile-overlay .gallery-thumbnail-track > button > video {
    border-radius: 0;
  }

  .gallery-video-mobile-overlay
    .gallery-thumbnail-track
    > button.gallery-mobile-video-thumbnail > span:first-child {
    background: #30343b;
    color: #fff;
  }

  .gallery-video-mobile-overlay
    .gallery-thumbnail-track
    > button[data-gallery-thumbnail-active='true'] {
    border: 0;
    border-radius: 0;
    box-shadow: none;
    outline: none;
  }

  .gallery-video-mobile-overlay
    .gallery-thumbnail-track
    > button[data-gallery-thumbnail-active='true']::after {
    position: absolute;
    inset: auto 0 0;
    height: 3px;
    background: #00a884;
    content: '';
    pointer-events: none;
  }

  .gallery-video-mobile-overlay .gallery-viewer-mobile-actions-footer > button {
    background: #f3f4f6;
  }

  .gallery-video-mobile-reactions {
    top: calc(var(--gallery-safe-top) + 11.5rem);
    bottom: auto;
  }

  .gallery-video-mobile-caption {
    width: 100%;
    max-width: none;
    margin-inline: 0;
  }

  .gallery-viewer-content .gallery-photo-mobile-full-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .acl-video-mobile-frame,
  .gallery-viewer-content .acl-video-mobile-media {
    width: 100%;
    height: 100%;
  }

  .acl-video-mobile-frame {
    overflow: hidden;
  }

  .gallery-viewer-content .acl-video-mobile-media {
    object-fit: cover;
  }

  .acl-video-controls {
    display: none !important;
  }

  .aceleres-whatsapp-media-viewer {
    --gallery-safe-top: env(safe-area-inset-top, 0px);
    --gallery-safe-bottom: env(safe-area-inset-bottom, 0px);
    width: 100%;
    height: 100%;
    max-height: 100%;
  }

  .gallery-viewer-header {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: calc(4.25rem + var(--gallery-safe-top));
    min-height: calc(4.25rem + var(--gallery-safe-top));
    padding: var(--gallery-safe-top) 0.5rem 0;
    gap: 0.25rem;
    border-bottom: 1px solid rgb(var(--border-weak));
  }

  .gallery-viewer-sender {
    order: 2;
    width: auto;
    padding: 0 0.25rem;
  }

  .gallery-viewer-sender :deep(.avatar) {
    display: none;
  }

  .gallery-toolbar {
    order: 3;
    width: auto;
    max-width: 11rem;
    min-width: 0;
    margin: 0;
    padding: 0;
    direction: ltr;
    justify-content: flex-start !important;
    overflow-x: visible;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
  }

  .gallery-viewer-close {
    order: 1;
    position: static;
    top: auto;
    right: auto;
    z-index: 2;
  }

  .gallery-toolbar > :deep(button),
  .gallery-toolbar > div {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  .gallery-action-show-chat,
  .gallery-action-pin,
  .gallery-action-download {
    display: none !important;
  }

  .gallery-reaction-popover {
    position: fixed;
    top: calc(var(--gallery-safe-top) + 4.5rem);
    right: 0.5rem;
    z-index: 2147483550;
    max-width: calc(100vw - 1rem);
    max-height: calc(100dvh - var(--gallery-safe-top) - 5rem);
  }

  .gallery-viewer-stage {
    position: relative;
    width: 100%;
  }

  .gallery-viewer-content {
    width: 100%;
    height: 100%;
    padding: 0;
  }

  .gallery-viewer-content img,
  .gallery-viewer-content video {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .gallery-viewer-nav {
    display: none;
  }

  .gallery-viewer-footer {
    height: calc(4.5rem + var(--gallery-safe-bottom));
    min-height: calc(4.5rem + var(--gallery-safe-bottom));
    padding-bottom: var(--gallery-safe-bottom);
  }

  .gallery-thumbnail-track {
    padding-inline: 0.125rem calc(50% - 1.75rem);
  }

  .gallery-thumbnail-track > button {
    width: 3.5rem;
    height: 3.5rem;
  }
}

@media (max-width: 767px) and (orientation: landscape) and (max-height: 500px) {
  .gallery-viewer-header {
    height: calc(2.75rem + var(--gallery-safe-top));
    min-height: calc(2.75rem + var(--gallery-safe-top));
    padding: var(--gallery-safe-top) 0.5rem 0;
  }

  .gallery-viewer-mobile-video-controls {
    display: none !important;
  }

  .gallery-viewer-footer {
    height: 2.75rem !important;
    min-height: 2.75rem !important;
    padding-block: 0.25rem !important;
  }

  .gallery-thumbnail-track > button,
  .gallery-video-mobile-overlay .gallery-thumbnail-track > button {
    width: 2.25rem;
    height: 2.25rem;
  }

  .gallery-viewer-mobile-actions-footer {
    padding: 0.125rem 0.75rem calc(0.125rem + var(--gallery-safe-bottom)) !important;
  }

  .gallery-viewer-mobile-actions-footer > button {
    width: 2.25rem;
    height: 2.25rem;
  }

  .gallery-viewer-content {
    padding: 0 !important;
  }
}
</style>
